import i18n from 'i18next';
import { initReactI18next } from 'react-i18next';
import LanguageDetector from 'i18next-browser-languagedetector';
import labelService from '../services/labelService';

// Default fallback translations (same as original)
const fallbackResources = {
  en: {
    translation: {
      // Navigation
      nav: {
        dashboard: 'Dashboard',
        pipeline: 'Pipeline',
        salesNotes: 'Notes',
        customers: 'Customers',
        leads: 'Leads',
        opportunities: 'Opportunities',
        activities: 'Activities',
        attachments: 'Attachments',
        contracts: 'Contracts',
        invoices: 'Invoices',
        aiInsights: 'Insights',
        businessCardScanner: 'Business Card Scanner',
        deletedRecords: 'Deleted Records',
        chatLogExplorer: 'Chat Log',
        ragManagement: 'RAG',
        aiConfiguration: 'AI Config',
        aiManagement: 'AI Management',
        lineManagement: 'Line Management',
        knowledgeCenter: 'Knowledge',
        tenantSettings: 'Tenant Settings',
        tenantMenuConfig: 'Menu Config',
        salesAssist: 'SalesAssist',
        supportTickets: 'Support',
        settings: 'Settings',
        adminTools: 'Admin',
        emailConfig: 'Email Config',
        emailLogs: 'Email Logs',
        emailManagement: 'Email Management',
        tenantEntityConfig: 'Entity Configuration',
        logout: 'Logout',
        profile: 'Profile'
      },
      // Common
      common: {
        save: 'Save',
        saving: 'Saving...',
        cancel: 'Cancel',
        delete: 'Delete',
        edit: 'Edit',
        add: 'Add',
        search: 'Search',
        loading: 'Loading...',
        error: 'Error',
        success: 'Success',
        warning: 'Warning',
        info: 'Information',
        confirm: 'Confirm',
        yes: 'Yes',
        no: 'No',
        close: 'Close',
        back: 'Back',
        next: 'Next',
        previous: 'Previous',
        submit: 'Submit',
        reset: 'Reset',
        refresh: 'Refresh',
        view: 'View',
        actions: 'Actions',
        selectAll: 'Select All',
        filter: 'Filter',
        filters: 'Filters',
        clearFilters: 'Clear Filters',
        relatedItems: 'Relationships'
      },
      // Authentication
      auth: {
        login: 'Sign In',
        register: 'Sign Up',
        logout: 'Logout',
        email: 'Email',
        password: 'Password',
        welcomeBack: 'Welcome to Flow Agentic Sales',
        username: 'Username',
        firstName: 'First Name',
        lastName: 'Last Name',
        confirmPassword: 'Confirm Password',
        rememberMe: 'Remember me',
        forgotPasswordLink: 'Forgot Password?',
        signingIn: 'Signing in...',
        creatingAccount: 'Creating account...',
        dontHaveAccount: "Don't have an account?",
        alreadyHaveAccount: 'Already have an account?',
        createAccount: 'Create Account',
        loginWithUsernameOrEmail: 'You can login with username or email',
        crmLogo: 'Flow Agentic Sales Logo',
        copyright: '© 2024',
        registrationFailed: 'Registration failed. Please try again.',
        usernameAutoGenerated: 'Username is auto-generated from your name. You can modify it if needed.',
        forgotPasswordModal: {
          enterUsernameFirst: 'Please enter your username first',
          resetFailed: 'Password reset failed',
          resetError: 'An error occurred while resetting password',
          resetTitle: 'Password Reset',
          resetSuccessMessage: 'Your temporary password is:',
          resetInstructions: 'Please use this temporary password to login and change it immediately.',
          fillPasswordAndClose: 'Fill Password and Close',
          title: 'Reset Password',
          instructions: 'Enter your email address and we\'ll send you a link to reset your password.',
          emailSentSuccess: 'If an account with that email exists, a password reset link has been sent.',
          sending: 'Sending...',
          sendResetLink: 'Send Reset Link'
        },
        // Forgot Password Flow
        forgotPasswordFlow: {
          title: 'Reset Password',
          description: 'Enter your email address and we\'ll send you a link to reset your password.',
          enterEmail: 'Please enter your email address',
          invalidEmail: 'Please enter a valid email address',
          sendFailed: 'Failed to send reset email. Please try again.',
          networkError: 'Network error. Please check your connection and try again.',
          sending: 'Sending...',
          sendResetLink: 'Send Reset Link',
          emailSent: 'Email Sent!',
          emailSentMessage: 'We\'ve sent a password reset link to {{email}}. Please check your email and follow the instructions to reset your password.',
          checkSpam: 'If you don\'t see the email, please check your spam folder.',
          emailSentSuccess: 'Password reset email sent successfully'
        },
        // Reset Password
        resetPassword: {
          title: 'Reset Your Password',
          subtitle: 'Enter your new password below',
          validating: 'Validating reset token...',
          invalidToken: 'Invalid Reset Link',
          invalidTokenMessage: 'This password reset link is invalid or has expired. Please request a new one.',
          tokenExpired: 'Reset token is invalid or has expired',
          networkError: 'Network error. Please try again.',
          passwordMismatch: 'Passwords do not match',
          weakPassword: 'Password does not meet requirements',
          resetFailed: 'Failed to reset password. Please try again.',
          resetting: 'Resetting Password...',
          resetPassword: 'Reset Password',
          success: 'Password Reset Successfully!',
          successMessage: 'Your password has been reset successfully. You will be redirected to the login page shortly.'
        },
        // Password Requirements
        passwordRequirements: 'Password Requirements:',
        passwordMinLength: 'At least 8 characters',
        passwordUpperCase: 'One uppercase letter',
        passwordLowerCase: 'One lowercase letter',
        passwordNumbers: 'One number',
        passwordSpecialChar: 'One special character',
        newPasswordPlaceholder: 'Enter new password',
        confirmPasswordPlaceholder: 'Confirm new password',
        emailPlaceholder: 'Enter your email address',
        backToLogin: 'Back to Login',
        validation: {
          firstNameRequired: 'First name is required',
          firstNameMinLength: 'First name must be at least 2 characters',
          lastNameMinLength: 'Last name must be at least 2 characters',
          emailRequired: 'Email is required',
          emailInvalid: 'Please enter a valid email address',
          usernameRequired: 'Username is required',
          usernameMinLength: 'Username must be at least 3 characters',
          usernameMaxLength: 'Username must be less than 50 characters',
          usernameInvalid: 'Username can only contain letters, numbers, dots, underscores, and hyphens',
          passwordRequired: 'Password is required',
          passwordMinLength: 'Password must be at least 8 characters long',
          passwordComplexity: 'Password must contain at least one uppercase letter, one lowercase letter, one number, and one special character',
          confirmPasswordRequired: 'Please confirm your password',
          passwordMismatch: 'Passwords do not match'
        },
        // User Profile specific translations
        userProfile: 'User Profile',
        manageAccount: 'Manage your account settings and preferences',
        backToDashboard: 'Back to Dashboard',
        profileInfo: 'Profile Information',
        updatePersonalDetails: 'Update your personal information',
        updateProfile: 'Update Profile',
        updating: 'Updating...',
        profileUpdated: 'Profile updated successfully',
        changePassword: 'Change Password',
        updateAccountPassword: 'Update your account password',
        currentPassword: 'Current Password',
        currentPasswordRequired: 'Current password is required',
        newPassword: 'New Password',
        newPasswordRequired: 'New password is required',
        newPasswordMinLength: 'New password must be at least 8 characters long',
        newPasswordComplexity: 'New password must contain at least one uppercase letter, one lowercase letter, one number, and one special character',
        confirmNewPassword: 'Confirm New Password',
        confirmPasswordRequired: 'Please confirm your new password',
        passwordsDoNotMatch: 'Passwords do not match',
        changing: 'Changing...',
        passwordChanged: 'Password changed successfully',
        accountInfo: 'Account Information',
        userID: 'User ID',
        roles: 'Roles',
        noRolesAssigned: 'No roles assigned',
        accountCreated: 'Account Created',
        lastUpdated: 'Last Updated',
        usernameNotEditable: 'Username cannot be changed after account creation',
        emailNotEditable: 'Email cannot be changed for security reasons',
        refreshData: 'Refresh Data',
        basicInfo: 'Basic Information',
        userType: 'User Type',
        administrator: 'Administrator',
        regularUser: 'Regular User',
        accountStatus: 'Account Status',
        active: 'Active',
        inactive: 'Inactive',
        rolesPermissions: 'Roles & Permissions',
        adminLevel: 'Admin Level',
        superAdmin: 'Super Admin',
        admin: 'Admin',
        assignedRepositories: 'Assigned Repositories',
        repositories: 'Repositories',
        timestamps: 'Timestamps',
        notAvailable: 'Not Available',
        lastLogin: 'Last Login',
        adminInfo: 'Administrator Information',
        adminRole: 'Admin Role',
        notSpecified: 'Not Specified',
        permissions: 'Permissions',
        fullAccess: 'Full Access',
        limitedAccess: 'Limited Access',
        phone: 'Phone',
        department: 'Department',
        jobTitle: 'Job Title',
        fixFields: 'Fix Fields',
        fixingFields: 'Fixing Fields...',
        confirmFixFields: 'This will add missing user profile fields to the database. Continue?'
      },
      // Language
      language: {
        selectLanguage: 'Select Language'
      },
      // Theme
      theme: {
        switchToLight: 'Switch to light mode',
        switchToDark: 'Switch to dark mode'
      },
      // Dashboard
      dashboard: {
        welcome: 'Welcome',
        description: 'Manage your customers and sales opportunities',
        addCustomer: 'Add Customer',
        addLead: 'Add Lead',
        viewAll: 'View All',
        noDataAvailable: 'No data available',
        recentActivities: 'Recent Activities',
        quickStats: 'Quick Stats',
        totalCustomers: 'Total Customers',
        totalLeads: 'Total Leads',
        totalOpportunities: 'Total Opportunities',
        totalRevenue: 'Total Revenue'
      },
      // Customers
      customers: {
        subtitle: 'Manage your customer relationships',
        importVCF: 'Import VCF',
        exportVCF: 'Export VCF',
        addCustomer: 'Add Customer',
        searchPlaceholder: 'Search customers...',
        allTypes: 'All Types',
        individual: 'Individual',
        company: 'Company',
        selectCustomersForExport: 'Select customers for export',
        exportSelected: 'Export Selected',
        customerNotFound: 'Customer not found',
        customerCode: 'Customer Code',
        customerType: 'Customer Type',
        personalInfo: 'Personal Information',
        companyInfo: 'Company Information',
        email: 'Email',
        phone: 'Phone',
        industry: 'Industry',
        address: 'Address',
        city: 'City',
        country: 'Country',
        website: 'Website',
        associatedPersons: 'Associated Persons',
        noAssociatedPersons: 'No associated persons found for this company',
        relatedLeads: 'Related Leads',
        relatedOpportunities: 'Related Opportunities',
        relatedActivities: 'Related Activities',
        salespersonInfo: 'Salesperson Information',
        assignedSalesperson: 'Assigned Salesperson',
        noSalesperson: 'No salesperson assigned',
        relatedItems: 'Related Items',
        leads: 'Leads',
        opportunities: 'Opportunities',
        activities: 'Activities',
        addToCustomers: 'Add to Customers',
        viewCompany: 'View Company',
        duplicateCompaniesFound: 'Duplicate Companies Found',
        addCompanyToCustomers: 'Add Company to Customers',
        duplicateCompanyWarning: 'We found existing company customers that might match. You can view them or create a new entry.',
        existingCompanies: 'Existing Companies',
        matchThaiName: 'Match: Thai Name',
        matchEngName: 'Match: English Name',
        viewThis: 'View This',
        orCreateNew: 'Or Create New Company',
        createNewCompanyDescription: 'If this is a different company, you can create a new company customer record.',
        createNewCompany: 'Create New Company',
        noDuplicateCompaniesFound: 'No duplicate companies found. This company will be added as a new customer.',
        companyDetails: 'Company Details',
        companyNameThai: 'Company Name (Thai)',
        companyNameEng: 'Company Name (English)',
        createCompanyCustomer: 'Create Company Customer',
        companyCreatedSuccess: 'Company added to customers successfully!',
        companyCreatedError: 'Failed to add company to customers. Please try again.',
        showingResults: 'Showing {{start}} to {{end}} of {{total}} customers',
        pageInfo: 'Page {{current}} of {{total}}'
      },
      // Errors
      errors: {
        unauthorized: 'Unauthorized access',
        insufficientPermissions: 'Insufficient permissions',
        customerDeleteWithRelationships: 'Cannot delete customer with existing relationships',
        validationError: 'Validation error',
        serverError: 'Server error'
      },
      // Leads
      leads: {
        subtitle: 'Manage and track your sales leads',
        aiLeadGeneration: 'AI Lead Generation',
        createLead: 'Create Lead',
        editLead: 'Edit Lead',
        createSubtitle: 'Add a new lead to track potential opportunities',
        editSubtitle: 'Update lead information and status',
        leadDetails: 'Lead Information and Details',
        basicInfo: 'Basic Information',
        title: 'Lead Title',
        leadSource: 'Lead Source',
        status: 'Status',
        priority: 'Priority',
        contactInfo: 'Contact Information',
        contactName: 'Contact Name',
        jobTitle: 'Job Title',
        email: 'Email',
        phone: 'Phone',
        contact: 'Contact',
        companyInfo: 'Company Information',
        companyName: 'Company Name',
        industry: 'Industry',
        companySize: 'Company Size',
        website: 'Website',
        company: 'Company',
        descriptionNotes: 'Description & Notes',
        description: 'Description',
        notes: 'Notes',
        scoringQualification: 'Scoring & Qualification',
        score: 'Score',
        qualificationLevel: 'Qualification Level',
        financialInfo: 'Financial Information',
        estimatedBudget: 'Estimated Budget',
        currency: 'Currency',
        budget: 'Budget',
        timeline: 'Timeline',
        expectedCloseDate: 'Expected Close Date',
        lastContactDate: 'Last Contact Date',
        nextFollowUpDate: 'Next Follow-up Date',
        nextFollowUp: 'Next Follow-up',
        conversionStatus: 'Conversion Status',
        convertedToOpportunity: 'Converted to Opportunity',
        convertedToCustomer: 'Converted to Customer',
        conversionDate: 'Conversion Date',
        salespersonInfo: 'Salesperson Information',
        assignedTo: 'Assigned To',
        salespersonEmail: 'Salesperson Email',
        auditInfo: 'Audit Information',
        statusNew: 'New',
        statusContacted: 'Contacted',
        statusQualified: 'Qualified',
        statusUnqualified: 'Unqualified',
        statusConverted: 'Converted',
        allStatuses: 'All Statuses',
        priorityLow: 'Low',
        priorityMedium: 'Medium',
        priorityHigh: 'High',
        priorityCritical: 'Critical',
        allPriorities: 'All Priorities',
        qualificationCold: 'Cold',
        qualificationWarm: 'Warm',
        qualificationHot: 'Hot',
        qualificationQualified: 'Qualified',
        allQualifications: 'All Qualification Levels',
        companySizeStartup: 'Startup',
        companySizeSmall: 'Small',
        companySizeMedium: 'Medium',
        companySizeLarge: 'Large',
        companySizeEnterprise: 'Enterprise',
        totalLeads: 'Total Leads',
        averageScore: 'Average Score',
        totalBudget: 'Total Budget',
        highPriority: 'High Priority',
        titlePlaceholder: 'e.g., Enterprise Software Solution',
        leadSourcePlaceholder: 'e.g., Website, Referral, Cold Call',
        contactNamePlaceholder: 'e.g., John Smith',
        jobTitlePlaceholder: 'e.g., IT Director',
        emailPlaceholder: 'e.g., john@company.com',
        phonePlaceholder: 'e.g., +1 (555) 123-4567',
        companyNamePlaceholder: 'e.g., Acme Corporation',
        industryPlaceholder: 'e.g., Technology, Healthcare',
        websitePlaceholder: 'e.g., https://www.company.com',
        descriptionPlaceholder: 'Describe the lead opportunity...',
        notesPlaceholder: 'Add any additional notes or observations...',
        searchPlaceholder: 'Search leads...',
        selectCompanySize: 'Select company size',
        selectQualification: 'Select qualification level',
        noLeads: 'No leads found',
        noLeadsDescription: 'Get started by creating your first lead.',
        leadNotFound: 'Lead not found',
        createdAt: 'Created At',
        updatedAt: 'Updated At',
        notesAndDescription: 'Notes & Description',
        leadScore: 'Lead Score',
        attachments: 'Attachments',
        requirements: 'Requirements',
        customer: 'Customer',
        scoreOutOf100: 'Score out of 100',
        additionalInfo: 'Additional Info',
        lastContact: 'Last Contact',
        nextFollowUpShort: 'Next Follow-up',
        toOpportunity: 'To Opportunity',
        toCustomer: 'To Customer',
        conversionDateLabel: 'Conversion Date',
        notSet: 'Not Set',
        yes: 'Yes',
        no: 'No',
        pickFromCustomer: 'Pick from Customer',
        pickFromCustomerSubtitle: 'Select an existing customer to fill contact information',
        searchCustomers: 'Search customers by name, email, or company...',
        noCustomersFound: 'No customers found matching your search',
        noCustomersAvailable: 'No customers available'
      },
      // Opportunities
      opportunities: {
        subtitle: 'Manage and track your sales opportunities',
        createOpportunity: 'Create Opportunity',
        editOpportunity: 'Edit Opportunity',
        createSubtitle: 'Add a new opportunity to track potential sales',
        editSubtitle: 'Update opportunity information',
        opportunityDetails: 'Opportunity Information and Details',
        basicInfo: 'Basic Information',
        title: 'Opportunity Title',
        description: 'Description',
        stage: 'Stage',
        priority: 'Priority',
        category: 'Category',
        customerInfo: 'Customer Information',
        customerName: 'Customer Name',
        contactName: 'Contact Name',
        contactEmail: 'Contact Email',
        contactPhone: 'Contact Phone',
        customer: 'Customer',
        financialInfo: 'Financial Information',
        value: 'Value',
        currency: 'Currency',
        probability: 'Probability',
        expectedRevenue: 'Expected Revenue',
        timeline: 'Timeline',
        expectedCloseDate: 'Expected Close Date',
        lastActivityDate: 'Last Activity Date',
        nextFollowUpDate: 'Next Follow-up Date',
        createdAt: 'Created At',
        additionalInfo: 'Additional Information',
        industry: 'Industry',
        leadSource: 'Lead Source',
        salesTeam: 'Sales Team',
        estimatedEffort: 'Estimated Effort',
        competitorInfo: 'Competitor Information',
        requirements: 'Requirements',
        nextStepAction: 'Next Step Action',
        notes: 'Notes',
        flags: 'Flags',
        isActive: 'Is Active',
        isHighValue: 'High Value',
        active: 'Active',
        highValue: 'High Value',
        stageLead: 'Lead',
        stageQualified: 'Qualified',
        stageProposal: 'Proposal',
        stageNegotiation: 'Negotiation',
        stageClosedWon: 'Closed Won',
        stageClosedLost: 'Closed Lost',
        allStages: 'All Stages',
        priorityLow: 'Low',
        priorityMedium: 'Medium',
        priorityHigh: 'High',
        priorityCritical: 'Critical',
        allPriorities: 'All Priorities',
        categoryNewBusiness: 'New Business',
        categoryExistingBusiness: 'Existing Business',
        categoryRenewal: 'Renewal',
        categoryUpsell: 'Upsell',
        allCategories: 'All Categories',
        industryTechnology: 'Technology',
        industryHealthcare: 'Healthcare',
        industryFinance: 'Finance',
        industryEducation: 'Education',
        industryRetail: 'Retail',
        industryManufacturing: 'Manufacturing',
        industryConstruction: 'Construction',
        industryOther: 'Other',
        selectIndustry: 'Select Industry',
        leadSourceWebsite: 'Website',
        leadSourceSocialMedia: 'Social Media',
        leadSourceEmailCampaign: 'Email Campaign',
        leadSourceColdCall: 'Cold Call',
        leadSourceReferral: 'Referral',
        leadSourceTradeShow: 'Trade Show',
        leadSourcePartner: 'Partner',
        leadSourceOther: 'Other',
        selectLeadSource: 'Select Lead Source',
        totalOpportunities: 'Total Opportunities',
        totalValue: 'Total Value',
        averageProbability: 'Average Probability',
        wonOpportunities: 'Won Opportunities',
        titlePlaceholder: 'e.g., Enterprise Software Solution',
        descriptionPlaceholder: 'Describe the sales opportunity...',
        customerNamePlaceholder: 'e.g., ABC Company Ltd.',
        contactNamePlaceholder: 'e.g., John Smith',
        contactEmailPlaceholder: 'e.g., john@company.com',
        contactPhonePlaceholder: 'e.g., +1 (555) 123-4567',
        valuePlaceholder: 'e.g., 100000',
        salesTeamPlaceholder: 'e.g., Sales Team A',
        estimatedEffortPlaceholder: 'e.g., 40 hours',
        competitorInfoPlaceholder: 'Describe competitor information...',
        requirementsPlaceholder: 'Describe customer requirements...',
        nextStepActionPlaceholder: 'Describe next step action...',
        notesPlaceholder: 'Add additional notes...',
        searchPlaceholder: 'Search opportunities...',
        hoursOrDays: 'hours or days',
        closeDate: 'Close Date',
        overview: 'Overview',
        details: 'Details',
        opportunityTimeline: 'Opportunity Timeline',
        opportunityCreated: 'Opportunity Created',
        lastActivity: 'Last Activity',
        nextFollowUp: 'Next Follow-up',
        expectedClose: 'Expected Close',
        generalNotes: 'General Notes',
        noOpportunitiesFound: 'No opportunities found',
        noOpportunities: 'No opportunities',
        clickToView: 'Click on any row to view details',
        noDescription: 'No description',
        noRequirements: 'No requirements',
        noCompetitorInfo: 'No competitor information',
        noNextStepAction: 'No next step action',
        noNotes: 'No notes',
        notFound: 'Opportunity not found'
      },
      // Notes
      salesNotes: {
        pagetitle: 'Notes',
        myNotes: 'My Notes',
        allNotes: 'All Notes',
        allUsers: 'All Users',
        viewingMyNotes: 'Viewing My Notes',
        viewingUserNotes: 'Viewing Notes by',
        viewingAllNotes: 'Viewing All Notes',
        note: 'note',
        notes: 'notes',
        backToMyNotes: 'Back to My Notes',
        searchPlaceholder: 'Search notes by content, tags, or author...',
        addNewNote: 'Add New Note',
        notePlaceholder: 'Write your note here...',
        tagsPlaceholder: 'Add tags (comma-separated)',
        addNote: 'Add Note',
        loading: 'Loading...',
        attachment: 'attachment',
        attachments: 'attachments',
        addAttachments: 'Add Attachments',
        analyzedWithEntities: 'Analyzed with created entities',
        analyzedNoEntities: 'Analyzed - no entities created',
        aiAnalysis: 'AI Analysis',
        completeAnalysis: 'Complete Analysis',
        customerAnalysis: 'Customers Only',
        leadAnalysis: 'Leads Only',
        opportunityAnalysis: 'Opportunities Only',
        activityAnalysis: 'Activities Only',
        progressInitializing: 'Setting up analysis...',
        progressAnalyzing: 'Preparing content for AI...',
        progressAIProcessing: 'AI is analyzing your note...',
        progressProcessingResults: 'Processing analysis results...',
        progressCompleted: 'Analysis completed!',
        view: 'View',
        download: 'Download',
        deleteAttachment: 'Delete Attachment',
        viewAttachments: 'View attachments',
        analysisResults: 'Analysis Results',
        hideLinks: 'Hide Links',
        showLinks: 'Show Links',
        deleteAnalysisResults: 'Delete analysis results from database',
        delete: 'Delete',
        createdEntities: 'Created Entities',
        foundButNotCreated: 'Found But Not Created',
        noSearchResults: 'No notes found matching your search.',
        clearSearch: 'Clear search',
        noNotes: 'No notes yet. Create your first note!',
        addAttachmentsToNote: 'Add attachments to note:',
        clickToSelectFiles: 'Click to select files',
        maxFileSize: 'Max file size: 50MB',
        selectedFiles: 'Selected Files:',
        downloadAttachment: 'Download Attachment',
        resize: 'Resize',
        loadingAttachment: 'Loading attachment...',
        aiAnalysisResults: 'AI Analysis Results',
        analysisStatistics: 'Analysis Statistics',
        confidence: 'Confidence',
        thai: 'Thai',
        english: 'English',
        bytes: 'Bytes',
        kb: 'KB',
        mb: 'MB',
        gb: 'GB',
        noteDeletedOrNotExists: 'Note was deleted or no longer exists',
        confirmDelete: 'Are you sure you want to delete this note?',
        noteDeletedWithAttachments: 'Note deleted with attachments',
        analysisInitializing: 'Initializing analysis...',
        analysisProcessing: 'Processing note...',
        analysisAIProcessing: 'AI is analyzing content...',
        analysisProcessingResults: 'Processing results...',
        analysisCompleted: 'Analysis completed successfully!',
        attachmentForSalesNote: 'Attachment for note',
        failedToUpload: 'Failed to upload',
        failedToUploadAttachments: 'Failed to upload attachments',
        failedToDownloadFile: 'Failed to download file',
        failedToDownloadAttachment: 'Failed to download attachment',
        confirmDeleteAttachment: 'Are you sure you want to delete this attachment?',
        failedToDeleteAttachment: 'Failed to delete attachment',
        failedToViewAttachment: 'Failed to view attachment',
        failedToLoadImage: 'Failed to load image',
        failedToLoadPDF: 'Failed to load PDF',
        failedToLoadTextFile: 'Failed to load text file',
        audioNotSupported: 'Your browser does not support audio playback.',
        videoNotSupported: 'Your browser does not support video playback.',
        previewNotAvailable: 'Preview not available for this file type. Please download to view.',
        downloadToView: 'Download to View',
        failedToLoadAttachment: 'Failed to load attachment'
      },
      // Activities
      activities: {
        title: 'Activities',
        subtitle: 'Track and manage all sales activities and follow-ups',
        newActivity: 'New Activity',
        editActivity: 'Edit Activity',
        createActivity: 'Create Activity',
        activityDetails: 'Activity Details',
        stats: {
          total: 'Total',
          completed: 'Completed',
          inProgress: 'In Progress',
          overdue: 'Overdue',
          upcoming: 'Upcoming',
          critical: 'Critical',
          planned: 'Planned'
        },
        activityType: 'Activity Type',
        subject: 'Subject',
        description: 'Description',
        priority: 'Priority',
        status: 'Status',
        dueDate: 'Due Date',
        startDate: 'Start Date',
        endDate: 'End Date',
        followUpDate: 'Follow-up Date',
        estimatedDuration: 'Estimated Duration (minutes)',
        actualDuration: 'Actual Duration (minutes)',
        location: 'Location',
        notes: 'Notes',
        outcome: 'Outcome',
        parentActivity: 'Parent Activity',
        assignedTo: 'Assigned To',
        pipelineStage: 'Pipeline Stage',
        types: {
          call: 'Call',
          meeting: 'Meeting',
          email: 'Email',
          task: 'Task',
          followUp: 'Follow-up',
          demo: 'Demo',
          proposal: 'Proposal',
          contractReview: 'Contract Review',
          negotiation: 'Negotiation',
          training: 'Training',
          support: 'Support',
          other: 'Other'
        },
        statuses: {
          planned: 'Planned',
          inProgress: 'In Progress',
          done: 'Done',
          onHold: 'On Hold',
          cancelled: 'Cancelled'
        },
        priorities: {
          low: 'Low',
          medium: 'Medium',
          high: 'High',
          critical: 'Critical'
        },
        entityTypes: {
          customer: 'Customer',
          lead: 'Lead',
          opportunity: 'Opportunity'
        },
        relatedEntities: 'Relationships',
        selectEntity: 'Select {{entityType}}',
        searchEntity: 'Search {{entityType}}...',
        noResultsFound: 'No results found',
        filters: 'Filters',
        clearAll: 'Clear All',
        searchActivities: 'Search activities...',
        allStatuses: 'All Statuses',
        allPriorities: 'All Priorities',
        allTypes: 'All Types',
        allEntityTypes: 'All Entity Types',
        deleteConfirm: 'Are you sure you want to delete this activity?',
        deleteSuccess: 'Activity deleted successfully',
        saveSuccess: 'Activity saved successfully',
        placeholders: {
          subject: 'Enter activity subject',
          description: 'Enter activity description',
          searchParentActivity: 'Search parent activities...',
          searchUsers: 'Search users...'
        },
        validation: {
          activityTypeRequired: 'Activity type is required',
          subjectRequired: 'Subject is required',
          dueDateRequired: 'Due date is required for Planned activities',
          startDateRequired: 'Start date is required for In Progress activities',
          startDateRequiredCompleted: 'Start date is required for completed activities'
        },
        noActivities: 'No activities found',
        loadingActivities: 'Loading activities...',
        failedToLoad: 'Failed to load activities',
        failedToSave: 'Failed to save activity',
        failedToDelete: 'Failed to delete activity',
        listView: 'List View',
        calendarView: 'Calendar View',
        year: 'Year',
        month: 'Month',
        week: 'Week',
        today: 'Today',
        legend: 'Legend',
        dueDatePlanned: 'Due Date (Planned)',
        followUpPlanned: 'Follow-up (Planned)',
        startDateInProgress: 'Start Date (In Progress)',
        startDateDone: 'Start Date (Done)',
        loadingCalendar: 'Loading calendar...',
        overdue: 'Overdue',
        noDueDate: 'No due date',
        unknown: 'Unknown',
        tryAdjustingFilters: 'Try adjusting your filters',
        createFirstActivity: 'Create your first activity to get started',
        previous: 'Previous',
        next: 'Next',
        page: 'Page',
        of: 'of',
        showing: 'Showing',
        to: 'to',
        results: 'results',
        dueDateLabel: 'Due Date:',
        startDateLabel: 'Start Date:',
        endDateLabel: 'End Date:',
        followUpLabel: 'Follow-up:',
        completedLabel: 'Completed:',
        duration: 'Duration',
        estimated: 'Estimated:',
        actual: 'Actual:',
        progress: 'Progress',
        activityCompleted: 'Activity completed successfully'
      },
      // Pipeline
      pipeline: {
        title: 'Sales Pipeline',
        subtitle: 'Manage your sales process through different stages',
        lead: 'Lead',
        opportunities: 'Opportunities',
        proposal: 'Proposal',
        negotiate: 'Negotiate',
        won: 'Won',
        lost: 'Lost',
        convertToOpportunity: 'Convert to Opportunity',
        convertSubtitle: 'Fill in additional details to convert this lead to an opportunity',
        leadInformation: 'Lead Information',
        noItems: 'No items in this stage',
        refresh: 'Refresh',
        converting: 'Converting...',
        selectOption: 'Select an option'
      },
      // Settings
      settings: {
        title: 'Settings',
        subtitle: 'Manage system settings and configurations',
        users: 'Users',
        companies: 'Companies',
        roles: 'Roles',
        defaults: 'Defaults',
        lists: 'Lists',
        deletedRecords: 'Deleted Records',
        userManagement: 'User Management',
        roleManagement: 'Role Management',
        companyManagement: 'Company Management',
        defaultValues: 'Default Values',
        listManagement: 'List Management',
        addUser: 'Add User',
        editUser: 'Edit User',
        createUser: 'Create User',
        addRole: 'Add Role',
        addCompany: 'Add Company',
        user: 'User',
        email: 'Email',
        phone: 'Phone',
        assignments: 'Assignments',
        created: 'Created',
        actions: 'Actions',
        customers: 'Customers',
        assignCustomers: 'Assign Customers',
        assignCustomersTo: 'Assign Customers to {{name}}',
        selectedCustomers: '{{count}} customers selected',
        searchUsers: 'Search users...',
        searchCompanies: 'Search companies...',
        searchCustomers: 'Search customers...',
        allRoles: 'All Roles',
        noRoles: 'No roles assigned',
        noDescription: 'No description',
        addNewItem: 'Add new item',
        companyName: 'Company Name',
        industry: 'Industry',
        city: 'City',
        country: 'Country',
        firstName: 'First Name',
        lastName: 'Last Name',
        username: 'Username',
        password: 'Password',
        confirmPassword: 'Confirm Password',
        defaultCustomerType: 'Default Customer Type',
        defaultCountry: 'Default Country',
        defaultCurrency: 'Default Currency',
        defaultLeadSource: 'Default Lead Source',
        defaultOpportunityStage: 'Default Opportunity Stage',
        defaultContractStatus: 'Default Contract Status',
        userCreated: 'User created successfully',
        userUpdated: 'User updated successfully',
        userDeleted: 'User deleted successfully',
        userRoleUpdated: 'User role updated successfully',
        customersAssigned: 'Customers assigned successfully',
        defaultValuesSaved: 'Default values saved successfully',
        listItemAdded: 'List item added successfully',
        listItemDeleted: 'List item deleted successfully',
        confirmDeleteUser: 'Are you sure you want to delete {{name}}?',
        confirmDeactivateUser: 'Are you sure you want to deactivate {{name}}?',
        confirmActivateUser: 'Are you sure you want to activate {{name}}?',
        userActivated: 'User activated successfully',
        userDeactivated: 'User deactivated successfully',
        status: 'Status',
        active: 'Active',
        inactive: 'Inactive',
        allStatuses: 'All Statuses',
        deactivateUser: 'Deactivate User',
        activateUser: 'Activate User',
        industries: 'Industries',
        leadSources: 'Lead Sources',
        opportunityStages: 'Opportunity Stages',
        contractStatuses: 'Contract Statuses',
        activityTypes: 'Activity Types',
        countries: 'Countries',
        currencies: 'Currencies',
        validation: {
          firstNameRequired: 'First name is required',
          lastNameRequired: 'Last name is required',
          usernameRequired: 'Username is required',
          emailRequired: 'Email is required',
          emailInvalid: 'Please enter a valid email address',
          passwordRequired: 'Password is required',
          passwordMismatch: 'Passwords do not match',
          roleRequired: 'At least one role is required'
        }
      },
      // AI Insights
      aiInsights: {
        title: 'AI Insights',
        subtitle: 'AI-powered analytics and insights',
        loading: 'Loading insights...',
        noData: 'No data available for analysis',
        error: 'Failed to load insights'
      },
      // Business Card Scanner
      businessCardScanner: {
        title: 'Business Card Scanner',
        subtitle: 'Scan and extract contact information from business cards',
        uploadCard: 'Upload Business Card',
        scanCard: 'Scan Card',
        clearResults: 'Clear Results',
        extractInfo: 'Extract Information',
        processing: 'Processing...',
        noFileSelected: 'No file selected',
        invalidFile: 'Invalid file format. Please upload an image.',
        extractionFailed: 'Failed to extract information',
        extractionSuccess: 'Information extracted successfully',
        detectedFields: 'Detected Fields',
        confidence: 'Confidence',
        editExtractedData: 'Edit Extracted Data',
        saveContact: 'Save Contact',
        contactSaved: 'Contact saved successfully',
        contactSaveFailed: 'Failed to save contact'
      },
      // Knowledge Center
      knowledgeCenter: {
        title: 'Knowledge Center',
        description: 'Central repository for FAQs, Product Catalog, Troubleshooting Guides, and Manuals',
        totalArticles: 'Total Articles',
        categories: 'Categories',
        recentArticles: 'Recent Articles',
        aiGenerated: 'AI Generated',
        searchPlaceholder: 'Search articles by title, content, or tags...',
        semanticSearch: 'Smart Search',
        semanticSearchPlaceholder: 'Ask a question or describe what you\'re looking for...',
        smartSearch: 'Smart Search',
        semanticResults: 'Smart Search Results',
        allCategories: 'All Categories',
        allSources: 'All Sources',
        aiAssistant: 'AI Assistant',
        aiAssistantPlaceholder: 'Ask a question about the knowledge base...',
        askAI: 'Ask AI',
        createArticle: 'Create Article',
        editArticle: 'Edit Article',
        createFirstArticle: 'Create First Article',
        noArticles: 'No Articles Found',
        noArticlesDesc: 'Start building your knowledge base by creating your first article.',
        articleTitle: 'Knowledge Center Article',
        content: 'Content',
        category: 'Category',
        tags: 'Tags',
        sourceType: 'Source Type',
        viewArticle: 'View Article',
        deleteConfirm: 'Are you sure you want to delete this article?',
        articleCreated: 'Article created successfully',
        articleUpdated: 'Article updated successfully',
        articleDeleted: 'Article deleted successfully',
        searchFailed: 'Search failed, please try again',
        loadFailed: 'Failed to load knowledge center data',
        relevanceScore: 'Relevance',
        foundResults: 'Found {count} relevant articles',
        noSearchResults: 'No articles found matching your search',
        tryDifferentQuery: 'Try a different search query or browse by category',
        titleRequired: 'Title is required',
        contentRequired: 'Content is required',
        categoryOptional: 'Category is optional',
        tagsOptional: 'Tags are optional (separate with commas)'
      },
      // Support Tickets
      supportTickets: {
        title: 'Support Tickets',
        subtitle: 'Manage support requests and issues',
        createTicket: 'Create Ticket',
        ticketDetails: 'Ticket Details',
        manageTicket: 'Manage Ticket',
        addUpdate: 'Add Update',
        updateTicket: 'Update Ticket',
        manage: 'Manage',
        confirmDelete: 'Are you sure you want to delete this ticket?',
        searchPlaceholder: 'Search tickets by title or description...',
        descriptionPlaceholder: 'Please describe your issue in detail...',
        updatePlaceholder: 'Add your update or additional information...',
        resolutionPlaceholder: 'Add resolution notes...',
        assignToPlaceholder: 'Enter User ID to assign',
        assignToHelper: 'Leave empty to unassign',
        attachmentsInfo: 'Add files, images, or links to support your ticket. Attachments will be uploaded when the ticket is created.',
        attachmentsAfterCreate: 'Attachments can be added after the ticket is created',
        fields: {
          title: 'Title',
          description: 'Description',
          category: 'Category',
          priority: 'Priority',
          status: 'Status',
          ticketId: 'Ticket ID',
          createdBy: 'Created By',
          assignedTo: 'Assigned To',
          created: 'Created',
          updated: 'Updated',
          resolved: 'Resolved',
          resolutionNotes: 'Resolution Notes',
          attachments: 'Attachments'
        },
        table: {
          ticket: 'Ticket',
          status: 'Status',
          priority: 'Priority',
          category: 'Category',
          createdBy: 'Created By',
          created: 'Created'
        },
        categories: {
          general: 'General',
          bugreport: 'Bug Report',
          featurerequest: 'Feature Request',
          accountissue: 'Account Issue',
          technicalsupport: 'Technical Support',
          training: 'Training',
          other: 'Other'
        },
        priorities: {
          low: 'Low',
          medium: 'Medium',
          high: 'High',
          critical: 'Critical'
        },
        statuses: {
          open: 'Open',
          inprogress: 'In Progress',
          waitingforresponse: 'Waiting for Response',
          resolved: 'Resolved',
          closed: 'Closed'
        },
        stats: {
          total: 'Total Tickets',
          open: 'Open',
          inProgress: 'In Progress',
          resolved: 'Resolved'
        },
        success: {
          created: 'Support ticket created successfully',
          createdWithAttachments: 'Support ticket created successfully with attachments',
          updated: 'Support ticket updated successfully',
          managed: 'Support ticket managed successfully',
          deleted: 'Support ticket deleted successfully',
          deletedWithAttachments: 'Support ticket and associated attachments deleted successfully'
        },
        errors: {
          fetchFailed: 'Failed to fetch support tickets',
          detailsFailed: 'Failed to fetch ticket details',
          fillRequired: 'Please fill in all required fields',
          updateRequired: 'Please enter an update',
          createFailed: 'Failed to create support ticket',
          updateFailed: 'Failed to update support ticket',
          manageFailed: 'Failed to manage support ticket',
          deleteFailed: 'Failed to delete support ticket'
        }
      }
    }
  },
  th: {
    translation: {
      // Navigation
      nav: {
        dashboard: 'แดชบอร์ด',
        pipeline: 'Pipeline',
        salesNotes: 'บันทึก',
        customers: 'ลูกค้า',
        leads: 'ลีดส์',
        opportunities: 'โอกาส',
        activities: 'กิจกรรม',
        attachments: 'ไฟล์',
        contracts: 'สัญญา',
        invoices: 'ใบแจ้งหนี้',
        aiInsights: 'วิเคราะห์',
        businessCardScanner: 'สแกนนามบัตร',
        chatLogExplorer: 'การสนทนา',
        ragManagement: 'RAG',
        aiConfiguration: 'AI',
        aiManagement: 'จัดการ AI',
        lineManagement: 'จัดการ Line',
        knowledgeCenter: 'ความรู้',
        salesAssist: 'SalesAssist',
        supportTickets: 'ช่วยเหลือ',
        settings: 'การตั้งค่า',
        tenantSettings: 'การตั้งค่าองค์กร',
        tenantMenuConfig: 'การตั้งค่าเมนู',
        adminTools: 'ผู้ดูแล',
        emailConfig: 'การตั้งค่าอีเมล',
        emailLogs: 'บันทึกอีเมล',
        emailManagement: 'จัดการอีเมล',
        tenantEntityConfig: 'การตั้งค่าเอนทิตี',
        logout: 'ออกจากระบบ',
        profile: 'โปรไฟล์'
      },
      // Common
      common: {
        save: 'บันทึก',
        saving: 'กำลังบันทึก...',
        cancel: 'ยกเลิก',
        delete: 'ลบ',
        edit: 'แก้ไข',
        add: 'เพิ่ม',
        search: 'ค้นหา',
        loading: 'กำลังโหลด...',
        error: 'ข้อผิดพลาด',
        success: 'สำเร็จ',
        warning: 'คำเตือน',
        info: 'ข้อมูล',
        confirm: 'ยืนยัน',
        yes: 'ใช่',
        no: 'ไม่',
        close: 'ปิด',
        back: 'กลับ',
        next: 'ถัดไป',
        previous: 'ก่อนหน้า',
        submit: 'ส่ง',
        reset: 'รีเซ็ต',
        refresh: 'รีเฟรช',
        view: 'ดู',
        actions: 'การดำเนินการ',
        selectAll: 'เลือกทั้งหมด',
        filter: 'กรอง',
        filters: 'ตัวกรอง',
        clearFilters: 'ล้างตัวกรอง',
        relatedItems: 'ความสัมพันธ์'
      },
      // Authentication
      auth: {
        login: 'เข้าสู่ระบบ',
        register: 'สมัครสมาชิก',
        logout: 'ออกจากระบบ',
        email: 'อีเมล',
        password: 'รหัสผ่าน',
        welcomeBack: 'ยินดีต้อนรับสู่ Flow Agentic Sales',
        username: 'ชื่อผู้ใช้',
        firstName: 'ชื่อ',
        lastName: 'นามสกุล',
        confirmPassword: 'ยืนยันรหัสผ่าน',
        rememberMe: 'จดจำฉัน',
        forgotPasswordLink: 'ลืมรหัสผ่าน?',
        signingIn: 'กำลังเข้าสู่ระบบ...',
        creatingAccount: 'กำลังสร้างบัญชี...',
        dontHaveAccount: 'ยังไม่มีบัญชี?',
        alreadyHaveAccount: 'มีบัญชีอยู่แล้ว?',
        createAccount: 'สร้างบัญชี',
        loginWithUsernameOrEmail: 'คุณสามารถเข้าสู่ระบบด้วยชื่อผู้ใช้หรืออีเมล',
        crmLogo: 'โลโก้ Flow Agentic Sales',
        copyright: '© 2024',
        registrationFailed: 'การสมัครสมาชิกล้มเหลว กรุณาลองใหม่อีกครั้ง',
        usernameAutoGenerated: 'ชื่อผู้ใช้จะถูกสร้างอัตโนมัติจากชื่อของคุณ คุณสามารถแก้ไขได้ตามต้องการ',
        forgotPasswordModal: {
          enterUsernameFirst: 'กรุณาใส่ชื่อผู้ใช้ก่อน',
          resetFailed: 'การรีเซ็ตรหัสผ่านล้มเหลว',
          resetError: 'เกิดข้อผิดพลาดในการรีเซ็ตรหัสผ่าน',
          resetTitle: 'รีเซ็ตรหัสผ่าน',
          resetSuccessMessage: 'รหัสผ่านชั่วคราวของคุณคือ:',
          resetInstructions: 'กรุณาใช้รหัสผ่านชั่วคราวนี้เพื่อเข้าสู่ระบบและเปลี่ยนทันที',
          fillPasswordAndClose: 'กรอกรหัสผ่านและปิด',
          title: 'รีเซ็ตรหัสผ่าน',
          instructions: 'กรุณากรอกอีเมลของคุณ และเราจะส่งลิงก์รีเซ็ตรหัสผ่านให้คุณ',
          emailSentSuccess: 'หากมีบัญชีที่ใช้อีเมลนี้ ลิงก์รีเซ็ตรหัสผ่านจะถูกส่งไปแล้ว',
          sending: 'กำลังส่ง...',
          sendResetLink: 'ส่งลิงก์รีเซ็ต'
        },
        // Forgot Password Flow
        forgotPasswordFlow: {
          title: 'รีเซ็ตรหัสผ่าน',
          description: 'กรุณากรอกอีเมลของคุณ และเราจะส่งลิงก์รีเซ็ตรหัสผ่านให้คุณ',
          enterEmail: 'กรุณากรอกอีเมลของคุณ',
          invalidEmail: 'กรุณากรอกอีเมลที่ถูกต้อง',
          sendFailed: 'ส่งอีเมลรีเซ็ตรหัสผ่านไม่สำเร็จ กรุณาลองใหม่อีกครั้ง',
          networkError: 'เกิดข้อผิดพลาดเครือข่าย กรุณาตรวจสอบการเชื่อมต่อและลองใหม่อีกครั้ง',
          sending: 'กำลังส่ง...',
          sendResetLink: 'ส่งลิงก์รีเซ็ต',
          emailSent: 'ส่งอีเมลแล้ว!',
          emailSentMessage: 'เราได้ส่งลิงก์รีเซ็ตรหัสผ่านไปยัง {{email}} แล้ว กรุณาตรวจสอบอีเมลและทำตามคำแนะนำเพื่อรีเซ็ตรหัสผ่าน',
          checkSpam: 'หากไม่เห็นอีเมล กรุณาตรวจสอบโฟลเดอร์สแปม',
          emailSentSuccess: 'ส่งอีเมลรีเซ็ตรหัสผ่านสำเร็จ'
        },
        // Reset Password
        resetPassword: {
          title: 'รีเซ็ตรหัสผ่านของคุณ',
          subtitle: 'กรุณากรอกรหัสผ่านใหม่ด้านล่าง',
          validating: 'กำลังตรวจสอบลิงก์รีเซ็ต...',
          invalidToken: 'ลิงก์รีเซ็ตไม่ถูกต้อง',
          invalidTokenMessage: 'ลิงก์รีเซ็ตรหัสผ่านนี้ไม่ถูกต้องหรือหมดอายุแล้ว กรุณาขอลิงก์ใหม่',
          tokenExpired: 'ลิงก์รีเซ็ตไม่ถูกต้องหรือหมดอายุแล้ว',
          networkError: 'เกิดข้อผิดพลาดเครือข่าย กรุณาลองใหม่อีกครั้ง',
          passwordMismatch: 'รหัสผ่านไม่ตรงกัน',
          weakPassword: 'รหัสผ่านไม่ตรงตามข้อกำหนด',
          resetFailed: 'รีเซ็ตรหัสผ่านไม่สำเร็จ กรุณาลองใหม่อีกครั้ง',
          resetting: 'กำลังรีเซ็ตรหัสผ่าน...',
          resetPassword: 'รีเซ็ตรหัสผ่าน',
          success: 'รีเซ็ตรหัสผ่านสำเร็จ!',
          successMessage: 'รหัสผ่านของคุณได้รับการรีเซ็ตเรียบร้อยแล้ว คุณจะถูกนำไปยังหน้าล็อกอินในไม่ช้า'
        },
        // Password Requirements
        passwordRequirements: 'ข้อกำหนดรหัสผ่าน:',
        passwordMinLength: 'อย่างน้อย 8 ตัวอักษร',
        passwordUpperCase: 'ตัวพิมพ์ใหญ่อย่างน้อย 1 ตัว',
        passwordLowerCase: 'ตัวพิมพ์เล็กอย่างน้อย 1 ตัว',
        passwordNumbers: 'ตัวเลขอย่างน้อย 1 ตัว',
        passwordSpecialChar: 'อักขระพิเศษอย่างน้อย 1 ตัว',
        newPasswordPlaceholder: 'กรอกรหัสผ่านใหม่',
        confirmPasswordPlaceholder: 'ยืนยันรหัสผ่านใหม่',
        emailPlaceholder: 'กรอกอีเมลของคุณ',
        backToLogin: 'กลับไปยังหน้าล็อกอิน',
        validation: {
          firstNameRequired: 'จำเป็นต้องใส่ชื่อ',
          firstNameMinLength: 'ชื่อต้องมีอย่างน้อย 2 ตัวอักษร',
          lastNameMinLength: 'นามสกุลต้องมีอย่างน้อย 2 ตัวอักษร',
          emailRequired: 'จำเป็นต้องใส่อีเมล',
          emailInvalid: 'กรุณาใส่อีเมลที่ถูกต้อง',
          usernameRequired: 'จำเป็นต้องใส่ชื่อผู้ใช้',
          usernameMinLength: 'ชื่อผู้ใช้ต้องมีอย่างน้อย 3 ตัวอักษร',
          usernameMaxLength: 'ชื่อผู้ใช้ต้องมีไม่เกิน 50 ตัวอักษร',
          usernameInvalid: 'ชื่อผู้ใช้สามารถมีได้เฉพาะตัวอักษร ตัวเลข จุด ขีดล่าง และขีดกลางเท่านั้น',
          passwordRequired: 'จำเป็นต้องใส่รหัสผ่าน',
          passwordMinLength: 'รหัสผ่านต้องมีอย่างน้อย 8 ตัวอักษร',
          passwordComplexity: 'รหัสผ่านต้องมีตัวอักษรพิมพ์ใหญ่ ตัวอักษรพิมพ์เล็ก ตัวเลข และอักขระพิเศษอย่างน้อย 1 ตัว',
          confirmPasswordRequired: 'กรุณายืนยันรหัสผ่านของคุณ',
          passwordMismatch: 'รหัสผ่านไม่ตรงกัน'
        },
        // User Profile specific translations
        userProfile: 'โปรไฟล์ผู้ใช้',
        manageAccount: 'จัดการการตั้งค่าบัญชีและความต้องการของคุณ',
        backToDashboard: 'กลับไปยังแดชบอร์ด',
        profileInfo: 'ข้อมูลโปรไฟล์',
        updatePersonalDetails: 'อัปเดตข้อมูลส่วนตัวของคุณ',
        updateProfile: 'อัปเดตโปรไฟล์',
        updating: 'กำลังอัปเดต...',
        profileUpdated: 'อัปเดตโปรไฟล์สำเร็จ',
        changePassword: 'เปลี่ยนรหัสผ่าน',
        updateAccountPassword: 'อัปเดตรหัสผ่านบัญชีของคุณ',
        currentPassword: 'รหัสผ่านปัจจุบัน',
        currentPasswordRequired: 'จำเป็นต้องใส่รหัสผ่านปัจจุบัน',
        newPassword: 'รหัสผ่านใหม่',
        newPasswordRequired: 'จำเป็นต้องใส่รหัสผ่านใหม่',
        newPasswordMinLength: 'รหัสผ่านใหม่ต้องมีอย่างน้อย 8 ตัวอักษร',
        newPasswordComplexity: 'รหัสผ่านใหม่ต้องมีตัวอักษรพิมพ์ใหญ่ ตัวอักษรพิมพ์เล็ก ตัวเลข และอักขระพิเศษอย่างน้อย 1 ตัว',
        confirmNewPassword: 'ยืนยันรหัสผ่านใหม่',
        confirmPasswordRequired: 'กรุณายืนยันรหัสผ่านใหม่ของคุณ',
        passwordsDoNotMatch: 'รหัสผ่านไม่ตรงกัน',
        changing: 'กำลังเปลี่ยน...',
        passwordChanged: 'เปลี่ยนรหัสผ่านสำเร็จ',
        accountInfo: 'ข้อมูลบัญชี',
        userID: 'รหัสผู้ใช้',
        roles: 'บทบาท',
        noRolesAssigned: 'ไม่มีบทบาทที่ได้รับมอบหมาย',
        accountCreated: 'สร้างบัญชีเมื่อ',
        lastUpdated: 'อัปเดตล่าสุดเมื่อ',
        usernameNotEditable: 'ไม่สามารถเปลี่ยนชื่อผู้ใช้หลังจากสร้างบัญชีแล้ว',
        emailNotEditable: 'ไม่สามารถเปลี่ยนอีเมลเพื่อความปลอดภัย',
        refreshData: 'รีเฟรชข้อมูล',
        basicInfo: 'ข้อมูลพื้นฐาน',
        userType: 'ประเภทผู้ใช้',
        administrator: 'ผู้ดูแลระบบ',
        regularUser: 'ผู้ใช้ทั่วไป',
        accountStatus: 'สถานะบัญชี',
        active: 'ใช้งานอยู่',
        inactive: 'ไม่ใช้งาน',
        rolesPermissions: 'บทบาทและสิทธิ์',
        adminLevel: 'ระดับผู้ดูแล',
        superAdmin: 'ผู้ดูแลสูงสุด',
        admin: 'ผู้ดูแล',
        assignedRepositories: 'คลังข้อมูลที่ได้รับมอบหมาย',
        repositories: 'คลังข้อมูล',
        timestamps: 'ข้อมูลเวลา',
        notAvailable: 'ไม่มีข้อมูล',
        lastLogin: 'เข้าสู่ระบบล่าสุด',
        adminInfo: 'ข้อมูลผู้ดูแลระบบ',
        adminRole: 'บทบาทผู้ดูแล',
        notSpecified: 'ไม่ได้ระบุ',
        permissions: 'สิทธิ์',
        fullAccess: 'สิทธิ์เต็ม',
        limitedAccess: 'สิทธิ์จำกัด',
        phone: 'โทรศัพท์',
        department: 'แผนก',
        jobTitle: 'ตำแหน่งงาน',
        fixFields: 'แก้ไขฟิลด์',
        fixingFields: 'กำลังแก้ไขฟิลด์...',
        confirmFixFields: 'การดำเนินการนี้จะเพิ่มฟิลด์โปรไฟล์ผู้ใช้ที่ขาดหายไปในฐานข้อมูล ต้องการดำเนินการต่อหรือไม่?'
      },
      // Language
      language: {
        selectLanguage: 'เลือกภาษา'
      },
      // Theme
      theme: {
        switchToLight: 'เปลี่ยนเป็นโหมดสว่าง',
        switchToDark: 'เปลี่ยนเป็นโหมดมืด'
      },
      // Dashboard
      dashboard: {
        welcome: 'ยินดีต้อนรับ',
        description: 'จัดการลูกค้าและโอกาสทางการขายของคุณ',
        addCustomer: 'เพิ่มลูกค้า',
        addLead: 'เพิ่มลีดส์',
        viewAll: 'ดูทั้งหมด',
        noDataAvailable: 'ไม่มีข้อมูล',
        recentActivities: 'กิจกรรมล่าสุด',
        quickStats: 'สถิติด่วน',
        totalCustomers: 'ลูกค้าทั้งหมด',
        totalLeads: 'ลีดส์ทั้งหมด',
        totalOpportunities: 'โอกาสทั้งหมด',
        totalRevenue: 'รายได้ทั้งหมด'
      },
      // Customers
      customers: {
        subtitle: 'จัดการความสัมพันธ์กับลูกค้า',
        importVCF: 'นำเข้า VCF',
        exportVCF: 'ส่งออก VCF',
        addCustomer: 'เพิ่มลูกค้า',
        searchPlaceholder: 'ค้นหาลูกค้า...',
        allTypes: 'ทุกประเภท',
        individual: 'บุคคล',
        company: 'บริษัท',
        selectCustomersForExport: 'เลือกลูกค้าสำหรับการส่งออก',
        exportSelected: 'ส่งออกที่เลือก',
        customerNotFound: 'ไม่พบลูกค้า',
        customerCode: 'รหัสลูกค้า',
        customerType: 'ประเภทลูกค้า',
        personalInfo: 'ข้อมูลส่วนตัว',
        companyInfo: 'ข้อมูลบริษัท',
        email: 'อีเมล',
        phone: 'โทรศัพท์',
        industry: 'อุตสาหกรรม',
        address: 'ที่อยู่',
        city: 'เมือง',
        country: 'ประเทศ',
        website: 'เว็บไซต์',
        associatedPersons: 'บุคคลที่เกี่ยวข้อง',
        noAssociatedPersons: 'ไม่พบบุคคลที่เกี่ยวข้องกับบริษัทนี้',
        relatedLeads: 'ลีดส์ที่เกี่ยวข้อง',
        relatedOpportunities: 'โอกาสที่เกี่ยวข้อง',
        relatedActivities: 'กิจกรรมที่เกี่ยวข้อง',
        salespersonInfo: 'ข้อมูลพนักงานขาย',
        assignedSalesperson: 'พนักงานขายที่ได้รับมอบหมาย',
        noSalesperson: 'ไม่มีพนักงานขายที่ได้รับมอบหมาย',
        relatedItems: 'รายการที่เกี่ยวข้อง',
        leads: 'ลีดส์',
        opportunities: 'โอกาส',
        activities: 'กิจกรรม',
        addToCustomers: 'เพิ่มเป็นลูกค้า',
        viewCompany: 'ดูบริษัท',
        duplicateCompaniesFound: 'พบบริษัทที่ซ้ำกัน',
        addCompanyToCustomers: 'เพิ่มบริษัทเป็นลูกค้า',
        duplicateCompanyWarning: 'เราพบบริษัทลูกค้าที่มีอยู่แล้วที่อาจตรงกัน คุณสามารถดูหรือสร้างรายการใหม่',
        existingCompanies: 'บริษัทที่มีอยู่',
        matchThaiName: 'ตรงกับ: ชื่อภาษาไทย',
        matchEngName: 'ตรงกับ: ชื่อภาษาอังกฤษ',
        viewThis: 'ดูรายการนี้',
        orCreateNew: 'หรือสร้างบริษัทใหม่',
        createNewCompanyDescription: 'หากนี่เป็นบริษัทที่แตกต่างกัน คุณสามารถสร้างรายการลูกค้าบริษัทใหม่',
        createNewCompany: 'สร้างบริษัทใหม่',
        noDuplicateCompaniesFound: 'ไม่พบบริษัทที่ซ้ำกัน บริษัทนี้จะถูกเพิ่มเป็นลูกค้าใหม่',
        companyDetails: 'รายละเอียดบริษัท',
        companyNameThai: 'ชื่อบริษัท (ภาษาไทย)',
        companyNameEng: 'ชื่อบริษัท (ภาษาอังกฤษ)',
        createCompanyCustomer: 'สร้างลูกค้าบริษัท',
        companyCreatedSuccess: 'เพิ่มบริษัทเป็นลูกค้าสำเร็จ!',
        companyCreatedError: 'ไม่สามารถเพิ่มบริษัทเป็นลูกค้าได้ กรุณาลองอีกครั้ง',
        showingResults: 'แสดง {{start}} ถึง {{end}} จาก {{total}} ลูกค้า',
        pageInfo: 'หน้า {{current}} จาก {{total}}'
      },
      // Errors
      errors: {
        unauthorized: 'การเข้าถึงไม่ได้รับอนุญาต',
        insufficientPermissions: 'สิทธิ์ไม่เพียงพอ',
        customerDeleteWithRelationships: 'ไม่สามารถลบลูกค้าที่มีความสัมพันธ์อยู่',
        validationError: 'ข้อผิดพลาดในการตรวจสอบ',
        serverError: 'ข้อผิดพลาดของเซิร์ฟเวอร์'
      },
      // Leads
      leads: {
        subtitle: 'จัดการและติดตามลีดส์การขาย',
        aiLeadGeneration: 'สร้างลีดส์ด้วย AI',
        createLead: 'สร้างลีดส์',
        editLead: 'แก้ไขลีดส์',
        createSubtitle: 'เพิ่มลีดส์ใหม่เพื่อติดตามโอกาสที่อาจเกิดขึ้น',
        editSubtitle: 'อัปเดตข้อมูลและสถานะลีดส์',
        leadDetails: 'ข้อมูลและรายละเอียดลีดส์',
        basicInfo: 'ข้อมูลพื้นฐาน',
        title: 'หัวข้อลีดส์',
        leadSource: 'แหล่งที่มาลีดส์',
        status: 'สถานะ',
        priority: 'ความสำคัญ',
        contactInfo: 'ข้อมูลการติดต่อ',
        contactName: 'ชื่อผู้ติดต่อ',
        jobTitle: 'ตำแหน่งงาน',
        email: 'อีเมล',
        phone: 'โทรศัพท์',
        contact: 'ติดต่อ',
        companyInfo: 'ข้อมูลบริษัท',
        companyName: 'ชื่อบริษัท',
        industry: 'อุตสาหกรรม',
        companySize: 'ขนาดบริษัท',
        website: 'เว็บไซต์',
        company: 'บริษัท',
        descriptionNotes: 'คำอธิบายและบันทึก',
        description: 'คำอธิบาย',
        notes: 'บันทึก',
        scoringQualification: 'การให้คะแนนและคุณสมบัติ',
        score: 'คะแนน',
        qualificationLevel: 'ระดับคุณสมบัติ',
        financialInfo: 'ข้อมูลทางการเงิน',
        estimatedBudget: 'งบประมาณโดยประมาณ',
        currency: 'สกุลเงิน',
        budget: 'งบประมาณ',
        timeline: 'ไทม์ไลน์',
        expectedCloseDate: 'วันที่คาดว่าจะปิด',
        lastContactDate: 'วันที่ติดต่อล่าสุด',
        nextFollowUpDate: 'วันที่ติดตามครั้งต่อไป',
        nextFollowUp: 'การติดตามครั้งต่อไป',
        conversionStatus: 'สถานะการแปลง',
        convertedToOpportunity: 'แปลงเป็นโอกาส',
        convertedToCustomer: 'แปลงเป็นลูกค้า',
        conversionDate: 'วันที่แปลง',
        salespersonInfo: 'ข้อมูลพนักงานขาย',
        assignedTo: 'มอบหมายให้',
        salespersonEmail: 'อีเมลพนักงานขาย',
        auditInfo: 'ข้อมูลการตรวจสอบ',
        statusNew: 'ใหม่',
        statusContacted: 'ติดต่อแล้ว',
        statusQualified: 'มีคุณสมบัติ',
        statusUnqualified: 'ไม่มีคุณสมบัติ',
        statusConverted: 'แปลงแล้ว',
        allStatuses: 'ทุกสถานะ',
        priorityLow: 'ต่ำ',
        priorityMedium: 'ปานกลาง',
        priorityHigh: 'สูง',
        priorityCritical: 'วิกฤต',
        allPriorities: 'ทุกความสำคัญ',
        qualificationCold: 'เย็น',
        qualificationWarm: 'อุ่น',
        qualificationHot: 'ร้อน',
        qualificationQualified: 'มีคุณสมบัติ',
        allQualifications: 'ทุกระดับคุณสมบัติ',
        companySizeStartup: 'สตาร์ทอัพ',
        companySizeSmall: 'เล็ก',
        companySizeMedium: 'ปานกลาง',
        companySizeLarge: 'ใหญ่',
        companySizeEnterprise: 'องค์กร',
        totalLeads: 'ลีดส์ทั้งหมด',
        averageScore: 'คะแนนเฉลี่ย',
        totalBudget: 'งบประมาณทั้งหมด',
        highPriority: 'ความสำคัญสูง',
        titlePlaceholder: 'เช่น โซลูชันซอฟต์แวร์องค์กร',
        leadSourcePlaceholder: 'เช่น เว็บไซต์ การแนะนำ การโทรเย็น',
        contactNamePlaceholder: 'เช่น จอห์น สมิธ',
        jobTitlePlaceholder: 'เช่น ผู้อำนวยการ IT',
        emailPlaceholder: 'เช่น john@company.com',
        phonePlaceholder: 'เช่น +66 2 123 4567',
        companyNamePlaceholder: 'เช่น บริษัท Acme จำกัด',
        industryPlaceholder: 'เช่น เทคโนโลยี สุขภาพ',
        websitePlaceholder: 'เช่น https://www.company.com',
        descriptionPlaceholder: 'อธิบายโอกาสลีดส์...',
        notesPlaceholder: 'เพิ่มบันทึกหรือข้อสังเกตเพิ่มเติม...',
        searchPlaceholder: 'ค้นหาลีดส์...',
        selectCompanySize: 'เลือกขนาดบริษัท',
        selectQualification: 'เลือกระดับคุณสมบัติ',
        noLeads: 'ไม่พบลีดส์',
        noLeadsDescription: 'เริ่มต้นด้วยการสร้างลีดส์แรกของคุณ',
        leadNotFound: 'ไม่พบลีดส์',
        createdAt: 'สร้างเมื่อ',
        updatedAt: 'อัปเดตเมื่อ',
        notesAndDescription: 'บันทึกและคำอธิบาย',
        leadScore: 'คะแนนลีดส์',
        attachments: 'ไฟล์แนบ',
        requirements: 'ข้อกำหนด',
        customer: 'ลูกค้า',
        scoreOutOf100: 'คะแนนจาก 100',
        additionalInfo: 'ข้อมูลเพิ่มเติม',
        lastContact: 'ติดต่อล่าสุด',
        nextFollowUpShort: 'ติดตามครั้งต่อไป',
        toOpportunity: 'เป็นโอกาส',
        toCustomer: 'เป็นลูกค้า',
        conversionDateLabel: 'วันที่แปลง',
        notSet: 'ไม่ได้ตั้งค่า',
        yes: 'ใช่',
        no: 'ไม่',
        pickFromCustomer: 'เลือกจากลูกค้า',
        pickFromCustomerSubtitle: 'เลือกลูกค้าที่มีอยู่เพื่อกรอกข้อมูลการติดต่อ',
        searchCustomers: 'ค้นหาลูกค้าตามชื่อ อีเมล หรือบริษัท...',
        noCustomersFound: 'ไม่พบลูกค้าที่ตรงกับการค้นหาของคุณ',
        noCustomersAvailable: 'ไม่มีลูกค้าที่ใช้ได้'
      },
      // Opportunities
      opportunities: {
        subtitle: 'จัดการและติดตามโอกาสการขาย',
        createOpportunity: 'สร้างโอกาส',
        editOpportunity: 'แก้ไขโอกาส',
        createSubtitle: 'เพิ่มโอกาสใหม่เพื่อติดตามการขายที่อาจเกิดขึ้น',
        editSubtitle: 'อัปเดตข้อมูลโอกาส',
        opportunityDetails: 'ข้อมูลและรายละเอียดโอกาส',
        basicInfo: 'ข้อมูลพื้นฐาน',
        title: 'หัวข้อ',
        description: 'คำอธิบาย',
        stage: 'ขั้นตอน',
        priority: 'ความสำคัญ',
        category: 'หมวดหมู่',
        customerInfo: 'ข้อมูลลูกค้า',
        customerName: 'ชื่อลูกค้า',
        contactName: 'ชื่อผู้ติดต่อ',
        contactEmail: 'อีเมลผู้ติดต่อ',
        contactPhone: 'โทรศัพท์ผู้ติดต่อ',
        customer: 'ลูกค้า',
        financialInfo: 'ข้อมูลทางการเงิน',
        value: 'มูลค่า',
        currency: 'สกุลเงิน',
        probability: 'ความน่าจะเป็น',
        expectedRevenue: 'รายได้ที่คาดหวัง',
        timeline: 'ไทม์ไลน์',
        expectedCloseDate: 'วันที่คาดว่าจะปิด',
        lastActivityDate: 'วันที่กิจกรรมล่าสุด',
        nextFollowUpDate: 'วันที่ติดตามครั้งต่อไป',
        createdAt: 'สร้างเมื่อ',
        additionalInfo: 'ข้อมูลเพิ่มเติม',
        industry: 'อุตสาหกรรม',
        leadSource: 'แหล่งที่มาลีดส์',
        salesTeam: 'ทีมขาย',
        estimatedEffort: 'ความพยายามโดยประมาณ',
        competitorInfo: 'ข้อมูลคู่แข่ง',
        requirements: 'ข้อกำหนด',
        nextStepAction: 'การดำเนินการขั้นตอนถัดไป',
        notes: 'บันทึก',
        flags: 'ธง',
        isActive: 'ใช้งานอยู่',
        isHighValue: 'มูลค่าสูง',
        active: 'ใช้งาน',
        highValue: 'มูลค่าสูง',
        stageLead: 'ลีดส์',
        stageQualified: 'มีคุณสมบัติ',
        stageProposal: 'ข้อเสนอ',
        stageNegotiation: 'การเจรจา',
        stageClosedWon: 'ปิดชนะ',
        stageClosedLost: 'ปิดแพ้',
        allStages: 'ทุกขั้นตอน',
        priorityLow: 'ต่ำ',
        priorityMedium: 'ปานกลาง',
        priorityHigh: 'สูง',
        priorityCritical: 'วิกฤต',
        allPriorities: 'ทุกความสำคัญ',
        categoryNewBusiness: 'ธุรกิจใหม่',
        categoryExistingBusiness: 'ธุรกิจที่มีอยู่',
        categoryRenewal: 'การต่ออายุ',
        categoryUpsell: 'การขายเพิ่ม',
        allCategories: 'ทุกหมวดหมู่',
        industryTechnology: 'เทคโนโลยี',
        industryHealthcare: 'สุขภาพ',
        industryFinance: 'การเงิน',
        industryEducation: 'การศึกษา',
        industryRetail: 'ค้าปลีก',
        industryManufacturing: 'การผลิต',
        industryConstruction: 'การก่อสร้าง',
        industryOther: 'อื่นๆ',
        selectIndustry: 'เลือกอุตสาหกรรม',
        leadSourceWebsite: 'เว็บไซต์',
        leadSourceSocialMedia: 'โซเชียลมีเดีย',
        leadSourceEmailCampaign: 'แคมเปญอีเมล',
        leadSourceColdCall: 'การโทรเย็น',
        leadSourceReferral: 'การแนะนำ',
        leadSourceTradeShow: 'งานแสดงสินค้า',
        leadSourcePartner: 'พันธมิตร',
        leadSourceOther: 'อื่นๆ',
        selectLeadSource: 'เลือกแหล่งที่มาลีดส์',
        totalOpportunities: 'โอกาสทั้งหมด',
        totalValue: 'มูลค่ารวม',
        averageProbability: 'ความน่าจะเป็นเฉลี่ย',
        wonOpportunities: 'โอกาสที่ชนะ',
        titlePlaceholder: 'เช่น โซลูชันซอฟต์แวร์องค์กร',
        descriptionPlaceholder: 'อธิบายโอกาสการขาย...',
        customerNamePlaceholder: 'เช่น บริษัท ABC จำกัด',
        contactNamePlaceholder: 'เช่น จอห์น สมิธ',
        contactEmailPlaceholder: 'เช่น john@company.com',
        contactPhonePlaceholder: 'เช่น +66 2 123 4567',
        valuePlaceholder: 'เช่น 100000',
        salesTeamPlaceholder: 'เช่น ทีมขาย A',
        estimatedEffortPlaceholder: 'เช่น 40 ชั่วโมง',
        competitorInfoPlaceholder: 'อธิบายข้อมูลคู่แข่ง...',
        requirementsPlaceholder: 'อธิบายข้อกำหนดลูกค้า...',
        nextStepActionPlaceholder: 'อธิบายการดำเนินการขั้นตอนถัดไป...',
        notesPlaceholder: 'เพิ่มบันทึกเพิ่มเติม...',
        searchPlaceholder: 'ค้นหาโอกาส...',
        hoursOrDays: 'ชั่วโมงหรือวัน',
        closeDate: 'วันที่ปิด',
        overview: 'ภาพรวม',
        details: 'รายละเอียด',
        opportunityTimeline: 'ไทม์ไลน์โอกาส',
        opportunityCreated: 'สร้างโอกาส',
        lastActivity: 'กิจกรรมล่าสุด',
        nextFollowUp: 'การติดตามครั้งต่อไป',
        expectedClose: 'คาดว่าจะปิด',
        generalNotes: 'บันทึกทั่วไป',
        noOpportunitiesFound: 'ไม่พบโอกาส',
        noOpportunities: 'ไม่มีโอกาส',
        clickToView: 'คลิกที่แถวใดก็ได้เพื่อดูรายละเอียด',
        noDescription: 'ไม่มีคำอธิบาย',
        noRequirements: 'ไม่มีข้อกำหนด',
        noCompetitorInfo: 'ไม่มีข้อมูลคู่แข่ง',
        noNextStepAction: 'ไม่มีการดำเนินการขั้นตอนถัดไป',
        noNotes: 'ไม่มีบันทึก',
        notFound: 'ไม่พบโอกาส'
      },
      // Notes
      salesNotes: {
        pagetitle: 'บันทึก',
        myNotes: 'บันทึกของฉัน',
        allNotes: 'บันทึกทั้งหมด',
        allUsers: 'ผู้ใช้ทั้งหมด',
        viewingMyNotes: 'ดูบันทึกของฉัน',
        viewingUserNotes: 'ดูบันทึกโดย',
        viewingAllNotes: 'ดูบันทึกทั้งหมด',
        note: 'บันทึก',
        notes: 'บันทึก',
        backToMyNotes: 'กลับไปยังบันทึกของฉัน',
        searchPlaceholder: 'ค้นหาบันทึกตามเนื้อหา แท็ก หรือผู้เขียน...',
        addNewNote: 'เพิ่มบันทึกใหม่',
        notePlaceholder: 'เขียนบันทึกการขายของคุณที่นี่...',
        tagsPlaceholder: 'เพิ่มแท็ก (คั่นด้วยจุลภาค)',
        addNote: 'เพิ่มบันทึก',
        loading: 'กำลังโหลด...',
        attachment: 'ไฟล์แนบ',
        attachments: 'ไฟล์แนบ',
        addAttachments: 'เพิ่มไฟล์แนบ',
        analyzedWithEntities: 'วิเคราะห์แล้วพร้อมสร้างเอนทิตี',
        analyzedNoEntities: 'วิเคราะห์แล้ว - ไม่มีการสร้างเอนทิตี',
        aiAnalysis: 'การวิเคราะห์ AI',
        completeAnalysis: 'การวิเคราะห์แบบสมบูรณ์',
        customerAnalysis: 'ลูกค้าเท่านั้น',
        leadAnalysis: 'ลีดส์เท่านั้น',
        opportunityAnalysis: 'โอกาสเท่านั้น',
        activityAnalysis: 'กิจกรรมเท่านั้น',
        progressInitializing: 'กำลังตั้งค่าการวิเคราะห์...',
        progressAnalyzing: 'กำลังเตรียมเนื้อหาสำหรับ AI...',
        progressAIProcessing: 'AI กำลังวิเคราะห์บันทึกของคุณ...',
        progressProcessingResults: 'กำลังประมวลผลผลการวิเคราะห์...',
        progressCompleted: 'การวิเคราะห์เสร็จสิ้น!',
        view: 'ดู',
        download: 'ดาวน์โหลด',
        deleteAttachment: 'ลบไฟล์แนบ',
        viewAttachments: 'ดูไฟล์แนบ',
        analysisResults: 'ผลการวิเคราะห์',
        hideLinks: 'ซ่อนลิงก์',
        showLinks: 'แสดงลิงก์',
        deleteAnalysisResults: 'ลบผลการวิเคราะห์จากฐานข้อมูล',
        delete: 'ลบ',
        createdEntities: 'เอนทิตีที่สร้าง',
        foundButNotCreated: 'พบแต่ไม่ได้สร้าง',
        noSearchResults: 'ไม่พบบันทึกที่ตรงกับการค้นหาของคุณ',
        clearSearch: 'ล้างการค้นหา',
        noNotes: 'ยังไม่มีบันทึก สร้างบันทึกแรกของคุณ!',
        addAttachmentsToNote: 'เพิ่มไฟล์แนบในบันทึก:',
        clickToSelectFiles: 'คลิกเพื่อเลือกไฟล์',
        maxFileSize: 'ขนาดไฟล์สูงสุด: 50MB',
        selectedFiles: 'ไฟล์ที่เลือก:',
        downloadAttachment: 'ดาวน์โหลดไฟล์แนบ',
        resize: 'ปรับขนาด',
        loadingAttachment: 'กำลังโหลดไฟล์แนบ...',
        aiAnalysisResults: 'ผลการวิเคราะห์ AI',
        analysisStatistics: 'สถิติการวิเคราะห์',
        confidence: 'ความเชื่อมั่น',
        thai: 'ไทย',
        english: 'อังกฤษ',
        bytes: 'ไบต์',
        kb: 'KB',
        mb: 'MB',
        gb: 'GB',
        noteDeletedOrNotExists: 'บันทึกถูกลบหรือไม่มีอยู่แล้ว',
        confirmDelete: 'คุณแน่ใจหรือต้องการลบบันทึกนี้?',
        noteDeletedWithAttachments: 'บันทึกถูกลบพร้อมไฟล์แนบ',
        analysisInitializing: 'กำลังเริ่มต้นการวิเคราะห์...',
        analysisProcessing: 'กำลังประมวลผลบันทึกการขาย...',
        analysisAIProcessing: 'AI กำลังวิเคราะห์เนื้อหา...',
        analysisProcessingResults: 'กำลังประมวลผลผลลัพธ์...',
        analysisCompleted: 'การวิเคราะห์เสร็จสิ้นเรียบร้อย!',
        attachmentForSalesNote: 'ไฟล์แนบสำหรับบันทึกการขาย',
        failedToUpload: 'อัปโหลดไม่สำเร็จ',
        failedToUploadAttachments: 'อัปโหลดไฟล์แนบไม่สำเร็จ',
        failedToDownloadFile: 'ดาวน์โหลดไฟล์ไม่สำเร็จ',
        failedToDownloadAttachment: 'ดาวน์โหลดไฟล์แนบไม่สำเร็จ',
        confirmDeleteAttachment: 'คุณแน่ใจหรือต้องการลบไฟล์แนบนี้?',
        failedToDeleteAttachment: 'ลบไฟล์แนบไม่สำเร็จ',
        failedToViewAttachment: 'ดูไฟล์แนบไม่สำเร็จ',
        failedToLoadImage: 'โหลดรูปภาพไม่สำเร็จ',
        failedToLoadPDF: 'โหลด PDF ไม่สำเร็จ',
        failedToLoadTextFile: 'โหลดไฟล์ข้อความไม่สำเร็จ',
        audioNotSupported: 'เบราว์เซอร์ของคุณไม่รองรับการเล่นเสียง',
        videoNotSupported: 'เบราว์เซอร์ของคุณไม่รองรับการเล่นวิดีโอ',
        previewNotAvailable: 'ไม่สามารถแสดงตัวอย่างไฟล์ประเภทนี้ได้ กรุณาดาวน์โหลดเพื่อดู',
        downloadToView: 'ดาวน์โหลดเพื่อดู',
        failedToLoadAttachment: 'โหลดไฟล์แนบไม่สำเร็จ'
      },
      // Activities
      activities: {
        title: 'กิจกรรม',
        subtitle: 'ติดตามและจัดการกิจกรรมการขายและการติดตามทั้งหมด',
        newActivity: 'กิจกรรมใหม่',
        editActivity: 'แก้ไขกิจกรรม',
        createActivity: 'สร้างกิจกรรม',
        activityDetails: 'รายละเอียดกิจกรรม',
        stats: {
          total: 'ทั้งหมด',
          completed: 'เสร็จสิ้น',
          inProgress: 'กำลังดำเนินการ',
          overdue: 'เกินกำหนด',
          upcoming: 'กำลังจะมาถึง',
          critical: 'วิกฤต',
          planned: 'วางแผนแล้ว'
        },
        activityType: 'ประเภทกิจกรรม',
        subject: 'หัวข้อ',
        description: 'คำอธิบาย',
        priority: 'ความสำคัญ',
        status: 'สถานะ',
        dueDate: 'วันที่ครบกำหนด',
        startDate: 'วันที่เริ่ม',
        endDate: 'วันที่สิ้นสุด',
        followUpDate: 'วันที่ติดตาม',
        estimatedDuration: 'ระยะเวลาที่คาดการณ์ (นาที)',
        actualDuration: 'ระยะเวลาจริง (นาที)',
        location: 'สถานที่',
        notes: 'บันทึก',
        outcome: 'ผลลัพธ์',
        parentActivity: 'กิจกรรมหลัก',
        assignedTo: 'มอบหมายให้',
        pipelineStage: 'ขั้นตอน Pipeline',
        types: {
          call: 'โทรศัพท์',
          meeting: 'ประชุม',
          email: 'อีเมล',
          task: 'งาน',
          followUp: 'การติดตาม',
          demo: 'สาธิต',
          proposal: 'ข้อเสนอ',
          contractReview: 'การตรวจสอบสัญญา',
          negotiation: 'การเจรจา',
          training: 'การฝึกอบรม',
          support: 'ช่วยเหลือ',
          other: 'อื่นๆ'
        },
        statuses: {
          planned: 'วางแผนแล้ว',
          inProgress: 'กำลังดำเนินการ',
          done: 'เสร็จสิ้น',
          onHold: 'ระงับ',
          cancelled: 'ยกเลิก'
        },
        priorities: {
          low: 'ต่ำ',
          medium: 'ปานกลาง',
          high: 'สูง',
          critical: 'วิกฤต'
        },
        entityTypes: {
          customer: 'ลูกค้า',
          lead: 'ลีดส์',
          opportunity: 'โอกาส'
        },
        relatedEntities: 'ความสัมพันธ์',
        selectEntity: 'เลือก {{entityType}}',
        searchEntity: 'ค้นหา {{entityType}}...',
        noResultsFound: 'ไม่พบผลลัพธ์',
        filters: 'ตัวกรอง',
        clearAll: 'ล้างทั้งหมด',
        searchActivities: 'ค้นหากิจกรรม...',
        allStatuses: 'ทุกสถานะ',
        allPriorities: 'ทุกความสำคัญ',
        allTypes: 'ทุกประเภท',
        allEntityTypes: 'ทุกประเภทเอนทิตี',
        deleteConfirm: 'คุณแน่ใจหรือไม่ที่จะลบกิจกรรมนี้?',
        deleteSuccess: 'ลบกิจกรรมสำเร็จ',
        saveSuccess: 'บันทึกกิจกรรมสำเร็จ',
        placeholders: {
          subject: 'ใส่หัวข้อกิจกรรม',
          description: 'ใส่คำอธิบายกิจกรรม',
          searchParentActivity: 'ค้นหากิจกรรมหลัก...',
          searchUsers: 'ค้นหาผู้ใช้...'
        },
        validation: {
          activityTypeRequired: 'ต้องระบุประเภทกิจกรรม',
          subjectRequired: 'ต้องระบุหัวข้อ',
          dueDateRequired: 'ต้องระบุวันที่ครบกำหนดสำหรับกิจกรรมที่วางแผนแล้ว',
          startDateRequired: 'ต้องระบุวันที่เริ่มสำหรับกิจกรรมที่กำลังดำเนินการ',
          startDateRequiredCompleted: 'ต้องระบุวันที่เริ่มสำหรับกิจกรรมที่เสร็จสิ้นแล้ว'
        },
        noActivities: 'ไม่พบกิจกรรม',
        loadingActivities: 'กำลังโหลดกิจกรรม...',
        failedToLoad: 'ไม่สามารถโหลดกิจกรรมได้',
        failedToSave: 'ไม่สามารถบันทึกกิจกรรมได้',
        failedToDelete: 'ไม่สามารถลบกิจกรรมได้',
        listView: 'มุมมองรายการ',
        calendarView: 'มุมมองปฏิทิน',
        year: 'ปี',
        month: 'เดือน',
        week: 'สัปดาห์',
        today: 'วันนี้',
        legend: 'คำอธิบายสัญลักษณ์',
        dueDatePlanned: 'วันที่ครบกำหนด (วางแผนแล้ว)',
        followUpPlanned: 'การติดตาม (วางแผนแล้ว)',
        startDateInProgress: 'วันที่เริ่ม (กำลังดำเนินการ)',
        startDateDone: 'วันที่เริ่ม (เสร็จสิ้น)',
        loadingCalendar: 'กำลังโหลดปฏิทิน...',
        overdue: 'เกินกำหนด',
        noDueDate: 'ไม่มีวันครบกำหนด',
        unknown: 'ไม่ทราบ',
        tryAdjustingFilters: 'ลองปรับตัวกรองของคุณ',
        createFirstActivity: 'สร้างกิจกรรมแรกของคุณเพื่อเริ่มต้น',
        previous: 'ก่อนหน้า',
        next: 'ถัดไป',
        page: 'หน้า',
        of: 'จาก',
        showing: 'แสดง',
        to: 'ถึง',
        results: 'ผลลัพธ์',
        dueDateLabel: 'วันที่ครบกำหนด:',
        startDateLabel: 'วันที่เริ่ม:',
        endDateLabel: 'วันที่สิ้นสุด:',
        followUpLabel: 'การติดตาม:',
        completedLabel: 'เสร็จสิ้น:',
        duration: 'ระยะเวลา',
        estimated: 'โดยประมาณ:',
        actual: 'จริง:',
        progress: 'ความคืบหน้า',
        activityCompleted: 'กิจกรรมเสร็จสิ้นเรียบร้อยแล้ว'
      },
      // Pipeline
      pipeline: {
        title: 'กระบวนการขาย',
        subtitle: 'จัดการกระบวนการขายผ่านขั้นตอนต่างๆ',
        lead: 'ลีดส์',
        opportunities: 'โอกาส',
        proposal: 'ข้อเสนอ',
        negotiate: 'เจรจา',
        won: 'ชนะ',
        lost: 'แพ้',
        convertToOpportunity: 'แปลงเป็นโอกาส',
        convertSubtitle: 'กรอกรายละเอียดเพิ่มเติมเพื่อแปลงลีดส์นี้เป็นโอกาส',
        leadInformation: 'ข้อมูลลีดส์',
        noItems: 'ไม่มีรายการในขั้นตอนนี้',
        refresh: 'รีเฟรช',
        converting: 'กำลังแปลง...',
        selectOption: 'เลือกตัวเลือก'
      },
      // Settings
      settings: {
        title: 'การตั้งค่า',
        subtitle: 'จัดการการตั้งค่าและคอนฟิกของระบบ',
        users: 'ผู้ใช้',
        companies: 'บริษัท',
        roles: 'บทบาท',
        defaults: 'ค่าเริ่มต้น',
        lists: 'รายการ',
        deletedRecords: 'บันทึกที่ถูกลบ',
        userManagement: 'การจัดการผู้ใช้',
        roleManagement: 'การจัดการบทบาท',
        companyManagement: 'การจัดการบริษัท',
        defaultValues: 'ค่าเริ่มต้น',
        listManagement: 'การจัดการรายการ',
        addUser: 'เพิ่มผู้ใช้',
        editUser: 'แก้ไขผู้ใช้',
        createUser: 'สร้างผู้ใช้',
        addRole: 'เพิ่มบทบาท',
        addCompany: 'เพิ่มบริษัท',
        user: 'ผู้ใช้',
        email: 'อีเมล',
        phone: 'โทรศัพท์',
        assignments: 'การมอบหมาย',
        created: 'สร้างเมื่อ',
        actions: 'การดำเนินการ',
        customers: 'ลูกค้า',
        assignCustomers: 'มอบหมายลูกค้า',
        assignCustomersTo: 'มอบหมายลูกค้าให้ {{name}}',
        selectedCustomers: 'เลือกลูกค้า {{count}} คน',
        searchUsers: 'ค้นหาผู้ใช้...',
        searchCompanies: 'ค้นหาบริษัท...',
        searchCustomers: 'ค้นหาลูกค้า...',
        allRoles: 'ทุกบทบาท',
        noRoles: 'ไม่มีบทบาทที่ได้รับมอบหมาย',
        noDescription: 'ไม่มีคำอธิบาย',
        addNewItem: 'เพิ่มรายการใหม่',
        companyName: 'ชื่อบริษัท',
        industry: 'อุตสาหกรรม',
        city: 'เมือง',
        country: 'ประเทศ',
        firstName: 'ชื่อ',
        lastName: 'นามสกุล',
        username: 'ชื่อผู้ใช้',
        password: 'รหัสผ่าน',
        confirmPassword: 'ยืนยันรหัสผ่าน',
        defaultCustomerType: 'ประเภทลูกค้าเริ่มต้น',
        defaultCountry: 'ประเทศเริ่มต้น',
        defaultCurrency: 'สกุลเงินเริ่มต้น',
        defaultLeadSource: 'แหล่งที่มาลีดส์เริ่มต้น',
        defaultOpportunityStage: 'ขั้นตอนโอกาสเริ่มต้น',
        defaultContractStatus: 'สถานะสัญญาเริ่มต้น',
        userCreated: 'สร้างผู้ใช้สำเร็จ',
        userUpdated: 'อัปเดตผู้ใช้สำเร็จ',
        userDeleted: 'ลบผู้ใช้สำเร็จ',
        userRoleUpdated: 'อัปเดตบทบาทผู้ใช้สำเร็จ',
        customersAssigned: 'มอบหมายลูกค้าสำเร็จ',
        defaultValuesSaved: 'บันทึกค่าเริ่มต้นสำเร็จ',
        listItemAdded: 'เพิ่มรายการสำเร็จ',
        listItemDeleted: 'ลบรายการสำเร็จ',
        confirmDeleteUser: 'คุณแน่ใจหรือไม่ที่จะลบ {{name}}?',
        confirmDeactivateUser: 'คุณแน่ใจหรือไม่ที่จะปิดใช้งาน {{name}}?',
        confirmActivateUser: 'คุณแน่ใจหรือไม่ที่จะเปิดใช้งาน {{name}}?',
        userActivated: 'เปิดใช้งานผู้ใช้สำเร็จ',
        userDeactivated: 'ปิดใช้งานผู้ใช้สำเร็จ',
        status: 'สถานะ',
        active: 'เปิดใช้งาน',
        inactive: 'ปิดใช้งาน',
        allStatuses: 'ทุกสถานะ',
        deactivateUser: 'ปิดใช้งานผู้ใช้',
        activateUser: 'เปิดใช้งานผู้ใช้',
        industries: 'อุตสาหกรรม',
        leadSources: 'แหล่งที่มาลีดส์',
        opportunityStages: 'ขั้นตอนโอกาส',
        contractStatuses: 'สถานะสัญญา',
        activityTypes: 'ประเภทกิจกรรม',
        countries: 'ประเทศ',
        currencies: 'สกุลเงิน',
        validation: {
          firstNameRequired: 'ต้องระบุชื่อ',
          lastNameRequired: 'ต้องระบุนามสกุล',
          usernameRequired: 'ต้องระบุชื่อผู้ใช้',
          emailRequired: 'ต้องระบุอีเมล',
          emailInvalid: 'กรุณากรอกอีเมลที่ถูกต้อง',
          passwordRequired: 'ต้องระบุรหัสผ่าน',
          passwordMismatch: 'รหัสผ่านไม่ตรงกัน',
          roleRequired: 'ต้องมีบทบาทอย่างน้อยหนึ่งบทบาท'
        }
      },
      // AI Insights
      aiInsights: {
        title: 'วิเคราะห์ AI',
        subtitle: 'การวิเคราะห์และข้อมูลเชิงลึกด้วย AI',
        loading: 'กำลังโหลดข้อมูลเชิงลึก...',
        noData: 'ไม่มีข้อมูลสำหรับการวิเคราะห์',
        error: 'ไม่สามารถโหลดข้อมูลเชิงลึกได้'
      },
      // Business Card Scanner
      businessCardScanner: {
        title: 'สแกนนามบัตร',
        subtitle: 'สแกนและดึงข้อมูลการติดต่อจากนามบัตร',
        uploadCard: 'อัปโหลดนามบัตร',
        scanCard: 'สแกนบัตร',
        clearResults: 'ล้างผลลัพธ์',
        extractInfo: 'ดึงข้อมูล',
        processing: 'กำลังประมวลผล...',
        noFileSelected: 'ไม่ได้เลือกไฟล์',
        invalidFile: 'รูปแบบไฟล์ไม่ถูกต้อง กรุณาอัปโหลดรูปภาพ',
        extractionFailed: 'ไม่สามารถดึงข้อมูลได้',
        extractionSuccess: 'ดึงข้อมูลสำเร็จ',
        detectedFields: 'ฟิลด์ที่ตรวจพบ',
        confidence: 'ความเชื่อมั่น',
        editExtractedData: 'แก้ไขข้อมูลที่ดึงมา',
        saveContact: 'บันทึกผู้ติดต่อ',
        contactSaved: 'บันทึกผู้ติดต่อสำเร็จ',
        contactSaveFailed: 'ไม่สามารถบันทึกผู้ติดต่อได้'
      },
      // Knowledge Center
      knowledgeCenter: {
        title: 'ศูนย์ความรู้',
        description: 'คลังข้อมูลกลางสำหรับคำถามที่พบบ่อย แคตตาล็อกผลิตภัณฑ์ คู่มือการแก้ไขปัญหา และคู่มือการใช้งาน',
        totalArticles: 'บทความทั้งหมด',
        categories: 'หมวดหมู่',
        recentArticles: 'บทความล่าสุด',
        aiGenerated: 'สร้างโดย AI',
        searchPlaceholder: 'ค้นหาบทความตามชื่อเรื่อง เนื้อหา หรือแท็ก...',
        semanticSearch: 'ค้นหาอัจฉริยะ',
        semanticSearchPlaceholder: 'ถามคำถามหรืออธิบายสิ่งที่คุณกำลังมองหา...',
        smartSearch: 'ค้นหาอัจฉริยะ',
        semanticResults: 'ผลการค้นหาอัจฉริยะ',
        allCategories: 'หมวดหมู่ทั้งหมด',
        allSources: 'แหล่งที่มาทั้งหมด',
        aiAssistant: 'ผู้ช่วย AI',
        aiAssistantPlaceholder: 'ถามคำถามเกี่ยวกับฐานความรู้...',
        askAI: 'ถาม AI',
        createArticle: 'สร้างบทความ',
        editArticle: 'แก้ไขบทความ',
        createFirstArticle: 'สร้างบทความแรก',
        noArticles: 'ไม่พบบทความ',
        noArticlesDesc: 'เริ่มสร้างฐานความรู้ของคุณด้วยการสร้างบทความแรก',
        articleTitle: 'ชื่อเรื่อง',
        content: 'เนื้อหา',
        category: 'หมวดหมู่',
        tags: 'แท็ก',
        sourceType: 'ประเภทแหล่งที่มา',
        viewArticle: 'ดูบทความ',
        deleteConfirm: 'คุณแน่ใจหรือว่าต้องการลบบทความนี้?',
        articleCreated: 'สร้างบทความสำเร็จ',
        articleUpdated: 'อัปเดตบทความสำเร็จ',
        articleDeleted: 'ลบบทความสำเร็จ',
        searchFailed: 'การค้นหาล้มเหลว กรุณาลองใหม่อีกครั้ง',
        loadFailed: 'ไม่สามารถโหลดข้อมูลศูนย์ความรู้ได้',
        relevanceScore: 'ความเกี่ยวข้อง',
        foundResults: 'พบ {count} บทความที่เกี่ยวข้อง',
        noSearchResults: 'ไม่พบบทความที่ตรงกับการค้นหาของคุณ',
        tryDifferentQuery: 'ลองใช้คำค้นหาอื่นหรือเรียกดูตามหมวดหมู่',
        titleRequired: 'ต้องระบุชื่อเรื่อง',
        contentRequired: 'ต้องระบุเนื้อหา',
        categoryOptional: 'หมวดหมู่เป็นทางเลือก',
        tagsOptional: 'แท็กเป็นทางเลือก (คั่นด้วยคอมมา)'
      },
      // Support Tickets
      supportTickets: {
        title: 'คำร้องขอความช่วยเหลือ',
        subtitle: 'จัดการคำร้องขอความช่วยเหลือและปัญหา',
        createTicket: 'สร้างคำร้อง',
        ticketDetails: 'รายละเอียดคำร้อง',
        manageTicket: 'จัดการคำร้อง',
        addUpdate: 'เพิ่มอัปเดต',
        updateTicket: 'อัปเดตคำร้อง',
        manage: 'จัดการ',
        confirmDelete: 'คุณแน่ใจหรือไม่ที่จะลบคำร้องนี้?',
        searchPlaceholder: 'ค้นหาคำร้องด้วยชื่อหรือคำอธิบาย...',
        descriptionPlaceholder: 'กรุณาอธิบายปัญหาของคุณอย่างละเอียด...',
        updatePlaceholder: 'เพิ่มอัปเดตหรือข้อมูลเพิ่มเติม...',
        resolutionPlaceholder: 'เพิ่มหมายเหตุการแก้ไข...',
        assignToPlaceholder: 'ใส่ User ID เพื่อมอบหมาย',
        assignToHelper: 'เว้นว่างเพื่อยกเลิกการมอบหมาย',
        attachmentsInfo: 'เพิ่มไฟล์ รูปภาพ หรือลิงก์เพื่อสนับสนุนคำร้องของคุณ ไฟล์แนบจะถูกอัปโหลดเมื่อสร้างคำร้องแล้ว',
        attachmentsAfterCreate: 'ไฟล์แนบสามารถเพิ่มได้หลังจากสร้างคำร้องแล้ว',
        fields: {
          title: 'ชื่อเรื่อง',
          description: 'คำอธิบาย',
          category: 'หมวดหมู่',
          priority: 'ความสำคัญ',
          status: 'สถานะ',
          ticketId: 'หมายเลขคำร้อง',
          createdBy: 'สร้างโดย',
          assignedTo: 'มอบหมายให้',
          created: 'สร้างเมื่อ',
          updated: 'อัปเดตเมื่อ',
          resolved: 'แก้ไขเมื่อ',
          resolutionNotes: 'หมายเหตุการแก้ไข',
          attachments: 'ไฟล์แนบ'
        },
        table: {
          ticket: 'คำร้อง',
          status: 'สถานะ',
          priority: 'ความสำคัญ',
          category: 'หมวดหมู่',
          createdBy: 'สร้างโดย',
          created: 'สร้างเมื่อ'
        },
        categories: {
          general: 'ทั่วไป',
          bugreport: 'รายงานข้อผิดพลาด',
          featurerequest: 'ขอคุณสมบัติใหม่',
          accountissue: 'ปัญหาบัญชี',
          technicalsupport: 'ช่วยเหลือทางเทคนิค',
          training: 'การฝึกอบรม',
          other: 'อื่นๆ'
        },
        priorities: {
          low: 'ต่ำ',
          medium: 'ปานกลาง',
          high: 'สูง',
          critical: 'วิกฤต'
        },
        statuses: {
          open: 'เปิด',
          inprogress: 'กำลังดำเนินการ',
          waitingforresponse: 'รอการตอบกลับ',
          resolved: 'แก้ไขแล้ว',
          closed: 'ปิด'
        },
        stats: {
          total: 'คำร้องทั้งหมด',
          open: 'เปิด',
          inProgress: 'กำลังดำเนินการ',
          resolved: 'แก้ไขแล้ว'
        },
        success: {
          created: 'สร้างคำร้องสำเร็จ',
          createdWithAttachments: 'สร้างคำร้องสำเร็จพร้อมไฟล์แนบ',
          updated: 'อัปเดตคำร้องสำเร็จ',
          managed: 'จัดการคำร้องสำเร็จ',
          deleted: 'ลบคำร้องสำเร็จ',
          deletedWithAttachments: 'ลบคำร้องและไฟล์แนบที่เกี่ยวข้องสำเร็จ'
        },
        errors: {
          fetchFailed: 'ไม่สามารถดึงข้อมูลคำร้องได้',
          detailsFailed: 'ไม่สามารถดึงรายละเอียดคำร้องได้',
          fillRequired: 'กรุณากรอกข้อมูลที่จำเป็น',
          updateRequired: 'กรุณาใส่อัปเดต',
          createFailed: 'ไม่สามารถสร้างคำร้องได้',
          updateFailed: 'ไม่สามารถอัปเดตคำร้องได้',
          manageFailed: 'ไม่สามารถจัดการคำร้องได้',
          deleteFailed: 'ไม่สามารถลบคำร้องได้'
        }
      }
    }
  }
};

// Dynamic resource loader that fetches from backend
const dynamicResourceLoader = {
  type: 'backend',
  init: () => {
    // No initialization needed
  },
  read: async (language, namespace, callback) => {
    try {
      console.log(`Loading dynamic translations for ${language}/${namespace}`);
      
      // Try to load from backend label service
      const backendLabels = await labelService.getLabels(language, false);
      
      if (backendLabels && Object.keys(backendLabels).length > 0) {
        console.log(`✅ Loaded ${Object.keys(backendLabels).length} labels from backend for ${language}`);
        callback(null, backendLabels);
      } else {
        // Fallback to static resources
        console.log(`⚠️ No backend labels found for ${language}, using fallback`);
        const fallback = fallbackResources[language];
        if (fallback) {
          callback(null, fallback.translation);
        } else {
          callback(null, fallbackResources.en.translation);
        }
      }
    } catch (error) {
      console.error(`❌ Failed to load dynamic translations for ${language}:`, error);
      
      // Fallback to static resources on error
      const fallback = fallbackResources[language];
      if (fallback) {
        callback(null, fallback.translation);
      } else {
        callback(null, fallbackResources.en.translation);
      }
    }
  }
};

// Initialize i18n with fallback resources
i18n
  .use(LanguageDetector)
  .use(initReactI18next)
  .init({
    // Use fallback resources directly
    resources: fallbackResources,
    fallbackLng: 'en',
    debug: process.env.NODE_ENV === 'development',
    
    interpolation: {
      escapeValue: false
    },

    // Language detection options
    detection: {
      order: ['localStorage', 'navigator', 'htmlTag'],
      caches: ['localStorage']
    }
  });

// Listen for language changes and refresh translations
i18n.on('languageChanged', async (lng) => {
  console.log(`🔄 Language changed to: ${lng}`);
  try {
    await refreshTranslations(lng);
  } catch (error) {
    console.error(`❌ Failed to refresh translations for ${lng}:`, error);
  }
});

// Custom function to refresh translations
export const refreshTranslations = async (language = i18n.language) => {
  try {
    console.log(`🔄 Refreshing translations for ${language}...`);
    
    // Use fallback resources
    const fallback = fallbackResources[language];
    if (fallback) {
      i18n.addResourceBundle(language, 'translation', fallback.translation, true, true);
      console.log(`✅ Updated i18n resources for ${language} with fallback data`);
    } else {
      i18n.addResourceBundle(language, 'translation', fallbackResources.en.translation, true, true);
      console.log(`✅ Updated i18n resources for ${language} with English fallback`);
    }
    
    // Try to load from backend in background (non-blocking)
    try {
      const backendLabels = await labelService.getLabels(language, true); // Force refresh
      if (backendLabels && Object.keys(backendLabels).length > 0) {
        // Update i18n resources with backend data
        i18n.addResourceBundle(language, 'translation', backendLabels, true, true);
        console.log(`✅ Updated i18n resources for ${language} with ${Object.keys(backendLabels).length} backend labels`);
      }
    } catch (backendError) {
      console.log(`⚠️ Backend labels not available for ${language}, using fallback only`);
    }
  } catch (error) {
    console.error(`❌ Failed to refresh translations for ${language}:`, error);
    
    // Use fallback resources on error
    const fallback = fallbackResources[language];
    if (fallback) {
      i18n.addResourceBundle(language, 'translation', fallback.translation, true, true);
    } else {
      i18n.addResourceBundle(language, 'translation', fallbackResources.en.translation, true, true);
    }
  }
};

// Function to add a new translation key dynamically
export const addTranslationKey = (language, key, value) => {
  try {
    const keys = key.split('.');
    let resource = i18n.getResourceBundle(language, 'translation') || {};
    
    // Navigate to the nested object
    let current = resource;
    for (let i = 0; i < keys.length - 1; i++) {
      if (!(keys[i] in current) || typeof current[keys[i]] !== 'object') {
        current[keys[i]] = {};
      }
      current = current[keys[i]];
    }
    
    // Set the value
    current[keys[keys.length - 1]] = value;
    
    // Update i18n resources
    i18n.addResourceBundle(language, 'translation', resource, true, true);
    console.log(`✅ Added translation key: ${key} = ${value} for ${language}`);
  } catch (error) {
    console.error(`❌ Failed to add translation key ${key}:`, error);
  }
};

// Function to remove a translation key dynamically
export const removeTranslationKey = (language, key) => {
  try {
    const keys = key.split('.');
    let resource = i18n.getResourceBundle(language, 'translation') || {};
    
    // Navigate to the parent object
    let current = resource;
    for (let i = 0; i < keys.length - 1; i++) {
      if (!(keys[i] in current) || typeof current[keys[i]] !== 'object') {
        return; // Key path doesn't exist
      }
      current = current[keys[i]];
    }
    
    // Delete the key
    delete current[keys[keys.length - 1]];
    
    // Update i18n resources
    i18n.addResourceBundle(language, 'translation', resource, true, true);
    console.log(`✅ Removed translation key: ${key} for ${language}`);
  } catch (error) {
    console.error(`❌ Failed to remove translation key ${key}:`, error);
  }
};

// Initialize with backend labels on startup
const initializeDynamicTranslations = async () => {
  const currentLanguage = i18n.language || 'en';
  
  try {
    console.log(`🚀 Initializing dynamic translations for ${currentLanguage}...`);
    
    // Wait for i18n to be ready
    await i18n.loadLanguages(currentLanguage);
    
    await refreshTranslations(currentLanguage);
  } catch (error) {
    console.error('❌ Failed to initialize dynamic translations:', error);
    // Ensure fallback is loaded
    const fallback = fallbackResources[currentLanguage] || fallbackResources.en;
    i18n.addResourceBundle(currentLanguage, 'translation', fallback.translation, true, true);
  }
};

// Initialize when module loads
initializeDynamicTranslations();

export default i18n;
